import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  mergeActivitiesByWbs,
  parseActivityUpdateResponse,
  applyActivityUpdates,
} from "../../lib/merge_activities";
import { predictOptimisticUpdate } from "../../lib/optimistic_activity";
import { useProject } from "../../contexts/ProjectContext";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import CommentsPanel from "./CommentsPanel";
import { ScrollArea } from "../ui/scroll-area";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Slider } from "../ui/slider";
import { Checkbox } from "../ui/checkbox";
import { Calendar as CalendarComponent } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  format,
  parseISO,
  addDays,
  addWeeks,
  isAfter,
  isBefore,
  isWithinInterval,
  differenceInDays,
} from "date-fns";
import {
  formatDateShort,
  formatDateLong,
  selectActivityStart,
  selectActivityFinish,
} from "../../lib/date_format";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Search,
  Loader2,
  Plus,
  FolderPlus,
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  CalendarIcon,
  Hash,
  Pencil,
  Save,
  RotateCcw,
  TrendingUp,
  Link,
  Link2,
  Unlink,
  ArrowRight,
  X,
  Trash2,
  PlayCircle,
  Users,
  Wrench,
  Package,
  Settings,
  Boxes,
  Eye,
  EyeOff,
  Lock,
  Copy,
  ClipboardPaste,
  GripVertical,
  MessageSquare,
} from "lucide-react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  getClientRect,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import wbsLogo from "../../assets/logos/wbs.png";
import activityLogo from "../../assets/logos/activity.png";
import RelationshipPicker from "./RelationshipPicker";
import ActivityPanel from "./ActivityPanel";
import BulkEditPanel from "./BulkEditPanel";
import ActualDatePickerBody from "./ActualDatePickerBody";
import {
  CLIPBOARD_COLUMNS as ACTIVITY_CLIPBOARD_COLUMNS,
  serializeToTSV as serializeActivitiesToTSV,
  serializeToHTML as serializeActivitiesToHTML,
  parseTSV as parseActivitiesTSV,
} from "../../lib/activity_clipboard";

// Layout constants
import { ROW_HEIGHT, WBS_ROW_HEIGHT_TOP, HEADER_HEIGHT } from "./constants";

// Default column widths (can be resized)
// Column widths - Actual Start/Finish columns removed, unified into Current Start/Finish with (a) indicator
const DEFAULT_COLUMN_WIDTHS = {
  links: 28,
  activityId: 72,
  activityName: 180,
  resources: 36,
  baselineStart: 85, // Baseline mode column
  currentStart: 90, // Display label "Start" (formerly "Current Start")
  duration: 50, // Placed right after Start so the row reads Start | Dur | Finish
  baselineFinish: 85, // Baseline mode column
  currentFinish: 90, // Display label "Finish" (formerly "Current Finish")
  actualStart: 90, // Standalone Actual Start column (display-only, never falls back to planned)
  actualFinish: 90, // Standalone Actual Finish column (display-only, never falls back to planned)
  expectedFinish: 90, // Manual expected finish date for forecasting
  progress: 80,
  float: 50,
  status: 72,
};

// Columns that can NEVER be hidden (always visible)
const ALWAYS_VISIBLE_COLUMNS = ["links", "activityId", "activityName"];

// Shared title/aria-label for the WBS and activity comment badges.
const commentBadgeLabel = (count) =>
  count ? `${count} comment${count === 1 ? "" : "s"} — click to open` : "Add a comment";

// Sortable wrapper for each activity row. Owns `useSortable` so the row
// renderer can stay a plain function (hooks-in-loops would be illegal).
// Passes drag attributes/listeners down to the row body via a render-prop
// so ONLY the grip handle activates the drag — inline edits, checkboxes,
// and the row click handler keep working unchanged.
function SortableActivityRow({
  activity,
  isCommittingThisRow = false,
  otherCommitInFlight = false,
  children,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: activity.id,
    data: { wbs_id: activity.wbs_id },
    // Commit-barrier: while ANOTHER row's reorder is in flight, this
    // row's drag is disabled. The row being committed stays interactive
    // so its optimistic position remains visible. Mental model:
    // "position is mine, schedule belongs to the engine."
    disabled: otherCommitInFlight,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Only the row currently being dragged gets the 0.5 ghost. Other
    // rows during a sibling's commit stay at full opacity — the prior
    // 0.6 dim made the whole table look greyed-out during the brief
    // 200-500ms commit window (user complaint: "a lot of gap").
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="select-none"
      data-activity-id={activity.id}
      aria-busy={isCommittingThisRow || undefined}
    >
      {children({
        attributes,
        listeners,
        isCommittingThisRow,
        otherCommitInFlight,
      })}
    </div>
  );
}

// Default hidden columns state (all visible by default)
const DEFAULT_HIDDEN_COLUMNS = {};

// Recursive count of descendant WBS nodes (excludes the node itself).
// Used by the delete-confirmation dialog to surface blast-radius.
function countDescendantWbs(node) {
  if (!node?.children?.length) return 0;
  return node.children.reduce((sum, c) => sum + 1 + countDescendantWbs(c), 0);
}

// Sum of activity_count across a node and all descendants. The `activity_count`
// field is set by the backend on every WBS node response.
function sumActivities(node) {
  if (!node) return 0;
  const own = node.activity_count || 0;
  const kids = node.children?.reduce((s, c) => s + sumActivities(c), 0) || 0;
  return own + kids;
}

const UnifiedScheduleView = ({
  // Bumped by parent (SchedulingScreen.handleActivityChange) after a server
  // mutation lands. Children re-fetch in place via the dependency below;
  // they do NOT re-mount, so scroll position and in-progress UI state survive.
  // Previously this was used as `key={`unified-${refreshKey}`}` which destroyed
  // the component on every edit — see plan kind-questing-storm.md Layer 1.
  refreshKey = 0,
  // Layer 4 — parent's persisted selection (sessionStorage-backed). When set,
  // we scroll the matching row into view ONCE per mount/schedule-change so
  // the user lands on the activity they were editing after a browser refresh.
  // We deliberately do NOT scroll on every change — clicking a row should not
  // re-center the viewport surprisingly.
  externalSelectedActivityId = null,
  onSelectActivity,
  onActivityChange,
  // Layer 2 — fast path for single-activity edits. Caller passes
  // [editedActivity, ...cascadedActivities] from the PUT response;
  // parent merges in place without re-fetching the schedule. When this
  // is provided, the response-using PUT sites prefer it over
  // onActivityChange (which triggers a full refetch).
  onActivitiesMerged = null,
  onWbsChange,
  onVerticalScroll,
  scrollRef,
  lookAheadWeeks = null,
  statusFilter = "all",
  dateRangeFilter = { from: null, to: null },
  activitySearch = "",
  criticalPathOnly = false,
  scenarioData = null,
  baselineComparisonData = null,
  // New props for baseline columns in schedule view (separate from baseline comparison mode)
  baselineColumnsData = null,
  selectedBaselineForColumns = null,
  expandedNodes: externalExpandedNodes = null,
  onExpandChange = null,
  onCloseBaseline = null,
  undoStack: externalUndoStack = null,
  setUndoStack: externalSetUndoStack = null,
  // Layout mode for responsive inline edit panels
  layoutMode = "top-bottom",
  // WBS copy/paste
  onSelectWbs = null,
  clipboardWbs = null,
  onCopyWbs = null,
  onPasteWbs = null,
  // Activity copy/paste — paste handler is async (creates rows server-side).
  onPasteActivities = null,
  // Callback to lift search changes to parent
  onActivitySearchChange = null,
  // Controlled-prop overrides for layout template support
  columnWidths: externalColumnWidths = null,
  onColumnWidthsChange = null,
  hiddenColumns: externalHiddenColumns = null,
  onHiddenColumnsChange = null,
  // Called by parent (SchedulingScreen) when an activity is deleted.
  // With activitiesByWbs derived from the `activities` prop, this just
  // signals the parent to filter allActivities; the derivation drops
  // the row in the same React tick. Plan zesty-coalescing-clarke.md.
  onActivityDeleted: externalOnActivityDeleted = null,
  // Called after a recursive WBS delete with the list of activity ids
  // the backend removed so the parent can drop them from allActivities.
  onActivitiesRemoved = null,
  // Single source of truth — owned by SchedulingScreen.allActivities
  // (with scenario overlay). activitiesByWbs below is derived from this.
  // Replaces the parallel fetch this component used to own. The
  // derivation guarantees that any mutation against allActivities
  // (from this child or from elsewhere) propagates to the table
  // automatically — no dual-store drift.
  activities = [],
  // Optimistic-mutation prop. Child calls with (prev => next) to mutate
  // the parent's activities list before the API responds. The parent
  // applies and re-derives; child sees the new prop on next render.
  onActivitiesOptimistic = null,
  // Comments: entity_id -> count map (owned by SchedulingScreen so the
  // bottom panel can update it live), badge-click opener for activity
  // rows, and the count-change callback for the WBS comments dialog.
  commentCounts = {},
  onOpenComments = null,
  onCommentCountChange = null,
}) => {
  const { activeSchedule, activeProject, can } = useProject();
  const { Action } = useAuth();

  // Two-tier RBAC, mirroring backend core/rbac.py via lib/auth.js `can`.
  // The project axis comes from useProject().can (bound to the active
  // project role); the org axis from user.role inside it.
  //   - EDIT_ACTIVITY  : add / edit activity fields (PLAN level)
  //   - EDIT_STRUCTURE : add / edit / delete WBS structure (PLAN level)
  //   - EDIT_ACTUALS   : progress + actual start/finish (COLLABORATE level)
  const canEditActivities = useCallback(
    () => can(Action.EDIT_ACTIVITY),
    [can, Action],
  );
  const canEditStructure = useCallback(
    () => can(Action.EDIT_STRUCTURE),
    [can, Action],
  );
  const canEditActuals = useCallback(
    () => can(Action.EDIT_ACTUALS),
    [can, Action],
  );

  // Data state
  const [wbsTree, setWbsTree] = useState([]);
  // activitiesByWbs is DERIVED from the `activities` prop. The parent
  // (SchedulingScreen.allActivities, with scenario overlay) is the
  // single source of truth. Every mutation that used to call
  // setActivitiesByWbs now routes through onActivitiesMerged or
  // onActivitiesOptimistic to the parent; this memo re-groups on the
  // next render. Plan zesty-coalescing-clarke.md.
  const activitiesByWbs = useMemo(() => {
    const grouped = {};
    for (const a of activities) {
      const wid = a.wbs_id;
      if (!grouped[wid]) grouped[wid] = [];
      grouped[wid].push(a);
    }
    return grouped;
  }, [activities]);
  const [loading, setLoading] = useState(false);

  // Tracks the schedule id of the last fetch so we know whether the current
  // run is the FIRST load for this schedule (auto-expand all WBS nodes) or a
  // refetch within the same schedule (preserve user's expansion state).
  // Without this guard, every refresh would clobber sharedExpandedNodes.
  const lastFetchedScheduleIdRef = useRef(null);

  // Layer 4 — has the one-shot scroll-restore fired for the current
  // schedule? Reset whenever the schedule changes so a switch-then-refresh
  // also scrolls. See effect below for the actual scroll.
  const hasRestoredScrollRef = useRef(false);
  const [internalExpandedNodes, setInternalExpandedNodes] = useState({});

  // Use external expanded nodes if provided, otherwise use internal state
  const expandedNodes =
    externalExpandedNodes !== null
      ? externalExpandedNodes
      : internalExpandedNodes;
  const setExpandedNodes = onExpandChange || setInternalExpandedNodes;

  // Selection state
  const [selectedWbsId, setSelectedWbsId] = useState(null);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  // Multi-select for bulk edit. Orthogonal to selectedActivityId — clicking
  // the row body still drives the detail panel; only the leading checkbox
  // toggles into this Set.
  const [selectedActivityIds, setSelectedActivityIds] = useState(
    () => new Set(),
  );
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  // Selections are per-schedule. The component does NOT remount on a schedule
  // switch, so without this reset a stale Set keeps the bulk toolbar showing a
  // count of rows that no longer exist (and "Select all visible" would union
  // stale + new ids).
  useEffect(() => {
    setSelectedActivityIds(new Set());
    setBulkEditOpen(false);
  }, [activeSchedule?.id]);

  // WBS context menu (right-click) state
  const [wbsContextMenu, setWbsContextMenu] = useState(null); // { x, y, node } | null
  const [wbsCommentsTarget, setWbsCommentsTarget] = useState(null); // node | null

  // Inline editing state for activities
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editingField, setEditingField] = useState(null); // 'start_date' | 'finish_date' | 'type'
  const [editValue, setEditValue] = useState("");
  const [markAsActual, setMarkAsActual] = useState(false); // Actualization checkbox state
  const [savingEdit, setSavingEdit] = useState(false);
  const [originalEditValue, setOriginalEditValue] = useState(""); // For undo - stores value before edit
  const [originalMarkAsActual, setOriginalMarkAsActual] = useState(false); // For undo - stores checkbox state
  const [editComment, setEditComment] = useState(""); // Optional comment for change tracking

  // Inline editing state for WBS
  const [editingWbsId, setEditingWbsId] = useState(null);
  const [editingWbsName, setEditingWbsName] = useState("");
  const [savingWbs, setSavingWbs] = useState(false);
  const [pendingDeleteWbs, setPendingDeleteWbs] = useState(null);
  const [isDeletingWbs, setIsDeletingWbs] = useState(false);

  // Refs for scroll sync
  const headerRef = useRef(null);

  // Relationships state
  const [relationships, setRelationships] = useState([]);
  const [showRelationshipsFor, setShowRelationshipsFor] = useState(null); // activity id
  const [loadingRelationships, setLoadingRelationships] = useState(false);
  const [showAddRelationDialog, setShowAddRelationDialog] = useState(false);

  const [addRelationType, setAddRelationType] = useState("predecessor"); // 'predecessor' | 'successor'
  const [newRelation, setNewRelation] = useState({
    activityId: "",
    relationshipType: "FS",
    lagDays: 0,
  });
  const [savingRelation, setSavingRelation] = useState(false);

  // Progress tracking state (MODULE O-1)
  const [editingProgressFor, setEditingProgressFor] = useState(null); // activity id
  const [progressUpdate, setProgressUpdate] = useState({
    actual_start: null,
    actual_finish: null,
    percent_complete: 0,
    remaining_duration: 0,
  });
  const [savingProgress, setSavingProgress] = useState(false);
  // Inline validation message for the percent-complete number input.
  // null when valid; string when out of range. Save button is disabled
  // while non-null. Acceptance criteria: reject with a clear message
  // (toast + inline) instead of silently clamping.
  const [progressUpdateError, setProgressUpdateError] = useState(null);

  // Resources state (MODULE O-2)
  const [resources, setResources] = useState([]); // All resources for schedule
  const [activityResources, setActivityResources] = useState({}); // activity_id -> assignments
  const [showResourcesFor, setShowResourcesFor] = useState(null); // activity id
  const [showAddResourceDialog, setShowAddResourceDialog] = useState(false);
  const [newResourceAssignment, setNewResourceAssignment] = useState({
    resource_id: "",
    units: 1,
  });
  const [savingResource, setSavingResource] = useState(false);
  const [showCreateResourceDialog, setShowCreateResourceDialog] =
    useState(false);
  const [newResource, setNewResource] = useState({
    name: "",
    resource_type: "person",
    unit: "",
  });

  // Calendar editing state
  const [editingCalendarFor, setEditingCalendarFor] = useState(null); // activity id
  const [selectedCalendarId, setSelectedCalendarId] = useState(""); // calendar_id for editing
  const [savingCalendar, setSavingCalendar] = useState(false);

  // Add WBS dialog
  const [showAddWbsDialog, setShowAddWbsDialog] = useState(false);
  const [isCreatingWbs, setIsCreatingWbs] = useState(false);
  const [newWbs, setNewWbs] = useState({ code: "", name: "", description: "" });
  const [addWbsAsChild, setAddWbsAsChild] = useState(false);
  const [addWbsParentId, setAddWbsParentId] = useState(null);

  // Column widths for resizable columns — controllable via props (for layout templates)
  const [internalColumnWidths, setInternalColumnWidths] = useState(
    DEFAULT_COLUMN_WIDTHS,
  );
  const columnWidths = externalColumnWidths ?? internalColumnWidths;
  const setColumnWidths = onColumnWidthsChange ?? setInternalColumnWidths;
  const [resizingColumn, setResizingColumn] = useState(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  // Activity Panel state (bottom sliding panel)
  const [showActivityPanel, setShowActivityPanel] = useState(false);

  // Session-level undo stack for activity changes
  // Use external state if provided, otherwise use internal state
  const [internalUndoStack, setInternalUndoStack] = useState([]);
  const undoStack =
    externalUndoStack !== null ? externalUndoStack : internalUndoStack;
  const setUndoStack = externalSetUndoStack || setInternalUndoStack;

  // Column visibility state (for settings dialog) — controllable via props (for layout templates)
  const [internalHiddenColumns, setInternalHiddenColumns] = useState(
    DEFAULT_HIDDEN_COLUMNS,
  );
  const hiddenColumns = externalHiddenColumns ?? internalHiddenColumns;
  const setHiddenColumns = onHiddenColumnsChange ?? setInternalHiddenColumns;
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [tempHiddenColumns, setTempHiddenColumns] = useState({}); // Temporary state for dialog

  // Column display names for the settings dialog
  const COLUMN_DISPLAY_NAMES = {
    resources: "Resources",
    baselineStart: "Baseline Start",
    currentStart: "Start",
    duration: "Duration",
    baselineFinish: "Baseline Finish",
    currentFinish: "Finish",
    actualStart: "Actual Start",
    actualFinish: "Actual Finish",
    expectedFinish: "Expected Finish",
    progress: "Progress",
    float: "Float",
    status: "Status",
  };

  // Get toggleable columns (exclude always-visible columns)
  const getToggleableColumns = useCallback(() => {
    return Object.keys(DEFAULT_COLUMN_WIDTHS).filter(
      (key) => !ALWAYS_VISIBLE_COLUMNS.includes(key),
    );
  }, []);

  // Open column settings dialog
  const handleOpenColumnSettings = () => {
    setTempHiddenColumns({ ...hiddenColumns }); // Copy current state to temp
    setShowColumnSettings(true);
  };

  // Cancel column settings (discard changes)
  const handleCancelColumnSettings = () => {
    setShowColumnSettings(false);
    setTempHiddenColumns({});
  };

  // Apply column settings
  const handleApplyColumnSettings = () => {
    setHiddenColumns({ ...tempHiddenColumns });
    setShowColumnSettings(false);
    setTempHiddenColumns({});
  };

  // Toggle column in temp state (for dialog checkboxes)
  const handleToggleColumn = (columnKey) => {
    setTempHiddenColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  // Check if a column is visible
  const isColumnVisible = useCallback(
    (columnKey) => {
      // Always visible columns cannot be hidden
      if (ALWAYS_VISIBLE_COLUMNS.includes(columnKey)) return true;
      // Baseline columns visible when:
      // 1. Baseline comparison mode is active (baselineComparisonData exists), OR
      // 2. A baseline is selected for column display in schedule view (baselineColumnsData exists)
      if (columnKey === "baselineStart" || columnKey === "baselineFinish") {
        return !!baselineComparisonData || !!baselineColumnsData;
      }
      return !hiddenColumns[columnKey];
    },
    [baselineComparisonData, baselineColumnsData, hiddenColumns],
  );

  // Get baseline data for an activity
  // This now checks both baselineComparisonData (baseline mode) and baselineColumnsData (schedule view with columns)
  const getBaselineDataForActivity = useCallback(
    (activityId) => {
      // First check baseline comparison data (baseline mode)
      if (baselineComparisonData?.activities) {
        const found = baselineComparisonData.activities.find(
          (a) => a.activity_id === activityId,
        );
        if (found) return found;
      }
      // Then check baseline columns data (schedule view with baseline columns)
      if (baselineColumnsData?.activities) {
        const found = baselineColumnsData.activities.find(
          (a) => a.activity_id === activityId,
        );
        if (found) return found;
      }
      return null;
    },
    [baselineComparisonData, baselineColumnsData],
  );

  // Column resize handlers
  const handleResizeStart = useCallback(
    (e, columnKey) => {
      e.preventDefault();
      e.stopPropagation();
      setResizingColumn(columnKey);
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = columnWidths[columnKey];

      const handleMouseMove = (moveEvent) => {
        const diff = moveEvent.clientX - resizeStartX.current;
        const newWidth = Math.max(30, resizeStartWidth.current + diff);
        setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
      };

      const handleMouseUp = () => {
        setResizingColumn(null);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [columnWidths],
  );

  // Add Activity dialog
  const [showAddActivityDialog, setShowAddActivityDialog] = useState(false);
  const [isCreatingActivity, setIsCreatingActivity] = useState(false);
  const [addActivityToWbsId, setAddActivityToWbsId] = useState(null);
  const [newActivity, setNewActivity] = useState({
    name: "",
    original_duration: 1,
    planned_start: "",
    planned_finish: "",
    status: "not_started",
    calendar_id: "",
  });

  // Calendar state (for activity-level calendar assignment)
  const [projectCalendars, setProjectCalendars] = useState([]);

  // Scroll sync ref
  const scrollContainerRef = useRef(null);

  // Expose scroll method via ref for external sync
  useEffect(() => {
    if (scrollRef) {
      scrollRef.current = {
        scrollTo: (scrollTop) => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollTop;
          }
        },
      };
    }
  }, [scrollRef]);

  // Fetch WBS tree, resources, and calendars. Activities are NOT fetched
  // here anymore — they come from the `activities` prop owned by
  // SchedulingScreen. This used to also fetch /activities/schedule/{id}
  // and group into activitiesByWbs, which created a parallel store that
  // drifted from the parent's allActivities after every bottom-panel
  // mutation. Plan zesty-coalescing-clarke.md.
  //
  // Resource bulk-assignments still need an activity id list — we derive
  // it from the `activities` prop so this effect re-runs (via the
  // `activities` dep) whenever the parent's list changes.
  const fetchData = useCallback(async () => {
    if (!activeSchedule?.id) return;

    // First load for THIS schedule? Auto-expand everything; on subsequent
    // refetches (refreshKey bumps, scenario toggles within the same
    // schedule) preserve whatever the user has expanded/collapsed.
    const isInitialLoadForSchedule =
      lastFetchedScheduleIdRef.current !== activeSchedule.id;
    lastFetchedScheduleIdRef.current = activeSchedule.id;

    setLoading(true);
    try {
      const [wbsResponse, resourcesResponse] = await Promise.all([
        api.get(`/wbs/schedule/${activeSchedule.id}/tree`),
        api
          .get(`/resources/schedule/${activeSchedule.id}`)
          .catch(() => ({ data: [] })),
      ]);

      setWbsTree(wbsResponse.data);
      setResources(resourcesResponse.data);

      if (scenarioData?.scenario_activities) {
        // Scenario mode — no resource assignments apply.
        setActivityResources({});
      } else {
        // Resource assignments — bulk fetch keyed off the current
        // activity ids (from the prop).
        const activityIds = activities.map((a) => a.id);
        if (activityIds.length > 0) {
          try {
            const assignmentsResponse = await api.post(
              "/resources/activities/bulk-assignments",
              activityIds,
            );
            setActivityResources(assignmentsResponse.data);
          } catch (e) {
            console.log("No resource assignments loaded");
            setActivityResources({});
          }
        }

        if (activeProject?.id) {
          try {
            const calendarsResponse = await api.get(
              `/calendars/project/${activeProject.id}`,
            );
            setProjectCalendars(calendarsResponse.data);
          } catch (e) {
            console.log("No calendars loaded");
            setProjectCalendars([]);
          }
        }
      }

      // Auto-expand all nodes — only on initial load for this schedule.
      if (isInitialLoadForSchedule) {
        const initialExpanded = {};
        const expandAll = (nodes) => {
          nodes.forEach((node) => {
            initialExpanded[node.id] = true;
            if (node.children) expandAll(node.children);
          });
        };
        expandAll(wbsResponse.data);
        setExpandedNodes(initialExpanded);
      }
    } catch (error) {
      console.error("Failed to fetch schedule data:", error);
      // Surface genuine read failures (5xx / network) — a silently empty tree
      // reads as "no data". Access-status responses stay quiet: 401 is owned
      // by the api interceptor (token clear + /login redirect), and 403/404
      // are the status→intent contract (DISABLE/HIDE — e.g. a membership
      // revoked mid-session), not load failures to toast about.
      const status = error?.response?.status;
      if (status !== 401 && status !== 403 && status !== 404) {
        toast.error("Failed to load schedule data — please refresh");
      }
    } finally {
      setLoading(false);
    }
    // activities is in the dep list so resource assignments stay synced
    // with the parent's source-of-truth list.
  }, [
    activeSchedule?.id,
    scenarioData,
    setExpandedNodes,
    activeProject?.id,
    refreshKey,
    activities,
  ]);

  // Re-fetch when scenario data changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Layer 4 — reset the scroll-restore latch when the user switches schedules
  // so a fresh schedule with a persisted selection re-scrolls.
  useEffect(() => {
    hasRestoredScrollRef.current = false;
  }, [activeSchedule?.id]);

  // Layer 4 — one-shot scroll-restore: after data loads, if the parent has
  // a persisted selection, scroll the matching row into view ONCE. We
  // intentionally do NOT scroll on every selection change — that would
  // re-center the viewport on every click, which is surprising.
  useEffect(() => {
    if (hasRestoredScrollRef.current) return;
    if (!externalSelectedActivityId) return;
    if (loading) return;
    const row = scrollContainerRef.current?.querySelector(
      `[data-activity-id="${externalSelectedActivityId}"]`,
    );
    if (row) {
      row.scrollIntoView({ block: "center", behavior: "auto" });
      hasRestoredScrollRef.current = true;
    }
    // activitiesByWbs is in deps because the row only exists once activities render.
  }, [externalSelectedActivityId, loading, activitiesByWbs]);

  // Close WBS context menu on click-outside or scroll
  useEffect(() => {
    if (!wbsContextMenu) return;
    const close = () => setWbsContextMenu(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
    };
  }, [wbsContextMenu]);

  const toggleExpand = (nodeId, e) => {
    e?.stopPropagation();
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const handleSelectWbs = (node, e) => {
    e?.stopPropagation();
    // Always select (no toggle-off on repeat click) so Ctrl+C stays reliable
    setSelectedWbsId(node.id);
    setSelectedActivityId(null);
    // Notify parent of WBS selection (for copy/paste keyboard shortcuts)
    onSelectWbs?.(node);
  };

  const handleSelectActivity = (activity, e) => {
    e?.stopPropagation();
    // Select the activity - popup dialog is now handled by parent SchedulingScreen
    setSelectedActivityId(activity.id);
    // Don't show bottom panel - using popup dialog instead
    // setShowActivityPanel(true);
    onSelectActivity?.(activity);
  };

  // Toggle a single activity in the multi-select Set. Stops propagation so the
  // checkbox click doesn't also fire the row body's handleSelectActivity.
  const toggleActivitySelection = (activityId, e) => {
    e?.stopPropagation();
    setSelectedActivityIds((prev) => {
      const next = new Set(prev);
      if (next.has(activityId)) {
        next.delete(activityId);
      } else {
        next.add(activityId);
      }
      return next;
    });
  };

  const clearActivitySelection = () => setSelectedActivityIds(new Set());

  // Remove a deleted activity from local state — two-layer reconciliation.
  //
  // Layer 1 (this function, fast path): optimistic in-place filter of
  //   activitiesByWbs so the row disappears in the same tick as the user
  //   confirms the delete. Avoids a perceptible flash of stale data
  //   between confirm-click and the parent's refetch completing.
  //
  // Layer 2 (parent SchedulingScreen.handleActivityDeleted, authoritative):
  //   bumps refreshKey, which triggers this component's data-fetch effect
  //   to GET /activities/schedule/{id} again. That re-fetch is the source
  //   of truth and overwrites activitiesByWbs with the server's view,
  //   reconciling any race or drift the optimistic filter could not catch
  //   (e.g., other cascaded changes from the CPM recalc).
  //
  // The selection clear is local because SchedulingScreen owns its own
  // selectedActivity state and clears it independently when notified.
  //
  // externalOnActivityDeleted is the prop callback into SchedulingScreen;
  // calling it triggers the Layer-2 refetch flow above. The redundancy is
  // intentional — Layer 1 hides the latency, Layer 2 guarantees correctness.
  // With activitiesByWbs derived from the `activities` prop, removing
  // an activity from the parent's allActivities causes the derivation
  // to drop it from the table in the same tick. This handler only
  // clears local-only state (selection highlight + bulk-select Set)
  // and forwards to the parent. Plan zesty-coalescing-clarke.md.
  const handleActivityDeleted = useCallback(
    (activityId) => {
      if (selectedActivityId === activityId) setSelectedActivityId(null);
      setSelectedActivityIds((prev) => {
        if (!prev.has(activityId)) return prev;
        const next = new Set(prev);
        next.delete(activityId);
        return next;
      });
      externalOnActivityDeleted?.(activityId);
    },
    [selectedActivityId, externalOnActivityDeleted],
  );

  // Undo handler (P9)
  const handleUndo = async (activityId) => {
    try {
      const response = await api.post(`/activities/${activityId}/undo`);
      toast.success("Change undone");

      // Merge reverted activity into parent allActivities; derivation
      // propagates back to activitiesByWbs.
      const revertedActivity = response.data;
      onActivitiesMerged?.([revertedActivity]);
      onActivityChange?.();
    } catch (error) {
      console.error("Undo failed:", error);
      toast.error(error.response?.data?.detail || "Failed to undo change");
    }
  };

  // Inline editing handlers for Activity
  const handleStartActivityEdit = (activity, field, e) => {
    e?.stopPropagation();
    if (!canEditActivities()) return;

    setEditingActivityId(activity.id);
    setEditingField(field);

    if (field === "start_date") {
      // If actual_start exists, use it and mark as actual; otherwise use planned_start
      if (activity.actual_start) {
        const val = activity.actual_start?.split("T")[0] || "";
        setEditValue(val);
        setOriginalEditValue(val); // Store original for undo
        setMarkAsActual(true);
        setOriginalMarkAsActual(true);
      } else {
        const val = activity.planned_start?.split("T")[0] || "";
        setEditValue(val);
        setOriginalEditValue(val); // Store original for undo
        setMarkAsActual(false);
        setOriginalMarkAsActual(false);
      }
    } else if (field === "finish_date") {
      // If actual_finish exists, use it and mark as actual; otherwise use planned_finish
      if (activity.actual_finish) {
        const val = activity.actual_finish?.split("T")[0] || "";
        setEditValue(val);
        setOriginalEditValue(val); // Store original for undo
        setMarkAsActual(true);
        setOriginalMarkAsActual(true);
      } else {
        const val = activity.planned_finish?.split("T")[0] || "";
        setEditValue(val);
        setOriginalEditValue(val); // Store original for undo
        setMarkAsActual(false);
        setOriginalMarkAsActual(false);
      }
    } else if (field === "type") {
      const val = activity.activity_type || "task";
      setEditValue(val);
      setOriginalEditValue(val); // Store original for undo
      setMarkAsActual(false);
      setOriginalMarkAsActual(false);
    }
  };

  // Dedicated save handler for the standalone Actual Start / Actual Finish columns.
  // Hits PUT /activities/{id}/progress so the server auto-syncs status, percent_complete,
  // remaining_duration, validates against project data_date, and triggers CPM recalc.
  // Self-contained: does NOT push to undoStack and does NOT touch the unified currentStart/currentFinish flow.
  const handleSaveActualEdit = async (activity, field, overrideValue) => {
    if (savingEdit) return;
    // Allow callers to pass the new value explicitly (e.g.
    // ActualDatePickerBody, which manages its own editValue state).
    // Falls back to the parent's editValue closure for legacy paths.
    const effectiveValue =
      overrideValue !== undefined ? overrideValue : editValue;
    // Ignore if value didn't change
    const current = activity[field] ? activity[field].split("T")[0] : "";
    if (current === (effectiveValue || "")) {
      handleCancelActivityEdit();
      return;
    }
    const newValue = effectiveValue || null;

    // Layer 5 — optimistic apply BEFORE the network call. Snapshot
    // pre-edit so we can revert on error. Goes through the parent
    // (onActivitiesOptimistic); derivation propagates back.
    const preEditActivity = activity;
    const optimisticUpdate = predictOptimisticUpdate(activity, field, newValue);
    onActivitiesOptimistic?.((prev) =>
      prev.map((a) =>
        a.id === optimisticUpdate.id ? { ...a, ...optimisticUpdate } : a,
      ),
    );
    setSavingEdit(true);

    try {
      const payload = { [field]: newValue };
      const response = await api.put(
        `/activities/${activity.id}/progress`,
        payload,
      );
      // Layer 2 — response is ActivityUpdateResponse: { activity, cascaded, cpm_ran }.
      const { activity: serverActivity, cascaded } =
        parseActivityUpdateResponse(response.data);
      const allUpdates = [serverActivity, ...cascaded];
      if (onActivitiesMerged) onActivitiesMerged(allUpdates);
      else onActivityChange?.();
      toast.success(
        field === "actual_start"
          ? "Actual Start updated"
          : "Actual Finish updated",
      );
    } catch (error) {
      // Layer 5 revert — restore pre-edit activity on failure.
      onActivitiesOptimistic?.((prev) =>
        prev.map((a) =>
          a.id === preEditActivity.id ? { ...a, ...preEditActivity } : a,
        ),
      );
      const message =
        error?.response?.data?.detail || "Failed to update actual date";
      toast.error(message);
    } finally {
      setSavingEdit(false);
      setEditingActivityId(null);
      setEditingField(null);
      setEditValue("");
    }
  };

  const handleSaveActivityEdit = async (activity) => {
    if (!editingField || !editingActivityId) return;

    // -- 1. Synchronous validation BEFORE the optimistic apply --------------
    if (editingField === "finish_date" && editValue) {
      const startStr = (
        activity.actual_start ||
        activity.planned_start ||
        activity.early_start ||
        ""
      ).split("T")[0];
      if (startStr && editValue < startStr) {
        toast.error("Finish date cannot be before Start date");
        return;
      }
    }
    if (editingField === "start_date" && editValue) {
      const finishStr = (
        activity.actual_finish ||
        activity.planned_finish ||
        activity.early_finish ||
        ""
      ).split("T")[0];
      if (finishStr && editValue > finishStr) {
        toast.error("Start date cannot be after Finish date");
        return;
      }
    }
    if (editingField === "duration") {
      const durationValue = parseInt(editValue, 10);
      if (isNaN(durationValue) || durationValue < 1) {
        toast.error("Duration must be at least 1 day");
        return;
      }
    }

    // -- 2. Build the request payload --------------------------------------
    let updateData = {};
    if (editingField === "start_date") {
      // Start date changed → send only date, server recalculates duration via calendar
      if (markAsActual) {
        updateData.actual_start = editValue;
        updateData.planned_start = editValue;
      } else {
        updateData.planned_start = editValue;
      }
    } else if (editingField === "finish_date") {
      // Finish date changed → send only date, server recalculates duration via calendar
      if (markAsActual) {
        updateData.actual_finish = editValue;
        updateData.planned_finish = editValue;
      } else {
        updateData.planned_finish = editValue;
      }
    } else if (editingField === "duration") {
      updateData.original_duration = parseInt(editValue, 10);
    } else if (editingField === "type") {
      updateData.activity_type = editValue;
    }
    if (editComment.trim()) {
      updateData.comment = editComment.trim();
    }

    // -- 3. Layer 5: optimistic render BEFORE the network call -------------
    // Snapshot pre-edit activity so we can revert on error. Predict the
    // post-edit row naïvely; the server will reconcile when the response
    // lands. Optimistic mutations go through the parent; derivation
    // propagates back to activitiesByWbs.
    const preEditActivity = activity;
    const optimisticUpdate = predictOptimisticUpdate(
      activity,
      editingField,
      editValue,
    );
    onActivitiesOptimistic?.((prev) =>
      prev.map((a) =>
        a.id === optimisticUpdate.id ? { ...a, ...optimisticUpdate } : a,
      ),
    );
    setEditingActivityId(null);
    setEditingField(null);
    setEditValue("");
    setMarkAsActual(false);
    setEditComment("");
    setSavingEdit(true);

    try {
      // Server handles all duration ↔ date sync using the activity's calendar
      // Layer 2 — response is ActivityUpdateResponse: { activity, cascaded, cpm_ran }.
      const response = await api.put(`/activities/${activity.id}`, updateData);
      const { activity: serverActivity, cascaded } =
        parseActivityUpdateResponse(response.data);

      // Update local state with server-returned values (server is source of truth).
      // Layer 2 — merge edited activity AND cascaded successors in one shot.
      // The optimistic value applied above is OVERWRITTEN here by the
      // authoritative server values, including any calendar-aware date
      // correction. Cascaded successors land in the same paint.
      const editedMerged = {
        ...serverActivity,
        ...updateData, // include fields the client sent (actual dates, etc.)
      };
      const allUpdates = [editedMerged, ...cascaded];

      // Layer 2 — single-RTT path: merge into parent state.
      // Derivation propagates the merged rows back to activitiesByWbs.
      if (onActivitiesMerged) onActivitiesMerged(allUpdates);
      else onActivityChange?.();

      // Build undo entry from updateData (the original editingField is
      // already cleared above for the optimistic close).
      const fieldDisplayNames = {
        planned_start: "Start",
        actual_start: "Start",
        planned_finish: "Finish",
        actual_finish: "Finish",
        original_duration: "Duration",
        activity_type: "Activity Type",
      };
      let undoField = null;
      let undoOldValue = null;
      let undoNewValue = null;
      if (updateData.actual_start !== undefined) {
        undoField = "actual_start";
        undoOldValue = preEditActivity.actual_start;
        undoNewValue = updateData.actual_start;
      } else if (updateData.planned_start !== undefined) {
        undoField = "planned_start";
        undoOldValue = preEditActivity.planned_start;
        undoNewValue = updateData.planned_start;
      } else if (updateData.actual_finish !== undefined) {
        undoField = "actual_finish";
        undoOldValue = preEditActivity.actual_finish;
        undoNewValue = updateData.actual_finish;
      } else if (updateData.planned_finish !== undefined) {
        undoField = "planned_finish";
        undoOldValue = preEditActivity.planned_finish;
        undoNewValue = updateData.planned_finish;
      } else if (updateData.original_duration !== undefined) {
        undoField = "original_duration";
        undoOldValue = preEditActivity.original_duration;
        undoNewValue = updateData.original_duration;
      } else if (updateData.activity_type !== undefined) {
        undoField = "activity_type";
        undoOldValue = preEditActivity.activity_type || "task";
        undoNewValue = updateData.activity_type;
      }
      if (undoField && setUndoStack) {
        setUndoStack((prev) => {
          const newEntry = {
            id: Date.now(),
            activityId: preEditActivity.id,
            activityName: preEditActivity.name,
            field: undoField,
            displayName: fieldDisplayNames[undoField] || undoField,
            oldValue: undoOldValue,
            newValue: undoNewValue,
            timestamp: new Date().toISOString(),
          };
          return [newEntry, ...prev].slice(0, 20);
        });
      }

      // Show appropriate success message
      if (updateData.original_duration !== undefined) {
        toast.success("Duration updated. Finish date recalculated.");
      } else if (
        updateData.planned_finish !== undefined ||
        updateData.actual_finish !== undefined
      ) {
        toast.success("Finish date updated");
      } else if (
        updateData.planned_start !== undefined ||
        updateData.actual_start !== undefined
      ) {
        toast.success("Start date updated. Finish date recalculated.");
      } else {
        toast.success("Activity updated");
      }
    } catch (error) {
      // Layer 5 revert: restore the pre-edit activity from local snapshot.
      console.error("Failed to update activity:", error);
      onActivitiesOptimistic?.((prev) =>
        prev.map((a) =>
          a.id === preEditActivity.id ? { ...a, ...preEditActivity } : a,
        ),
      );
      const message =
        error?.response?.data?.detail || "Failed to update activity";
      toast.error(message);
    } finally {
      setSavingEdit(false);
      setEditingActivityId(null);
      setEditingField(null);
      setEditValue("");
      setMarkAsActual(false);
      setEditComment("");
    }
  };

  const handleCancelActivityEdit = () => {
    setEditingActivityId(null);
    setEditingField(null);
    setEditValue("");
    setMarkAsActual(false);
    setOriginalEditValue("");
    setOriginalMarkAsActual(false);
    setEditComment("");
  };

  // Undo handler - reverts to original value without API call
  const handleUndoActivityEdit = () => {
    setEditValue(originalEditValue);
    setMarkAsActual(originalMarkAsActual);
  };

  // Check if current edit value differs from original (for showing undo button)
  const hasUnsavedChanges =
    editValue !== originalEditValue || markAsActual !== originalMarkAsActual;

  // Inline editing handlers for WBS
  const handleStartWbsEdit = (wbs, e) => {
    e?.stopPropagation();
    if (!canEditStructure()) return;

    setEditingWbsId(wbs.id);
    setEditingWbsName(wbs.name);
  };

  const handleSaveWbsEdit = async (wbs) => {
    if (!canEditStructure()) return; // defense-in-depth (backend: EDIT_STRUCTURE)
    if (!editingWbsName.trim()) {
      toast.error("WBS name cannot be empty");
      return;
    }

    setSavingWbs(true);
    try {
      await api.patch(`/wbs/${wbs.id}`, { name: editingWbsName });

      // Update local state
      const updateTree = (nodes) => {
        return nodes.map((node) => {
          if (node.id === wbs.id) {
            return { ...node, name: editingWbsName };
          }
          if (node.children) {
            return { ...node, children: updateTree(node.children) };
          }
          return node;
        });
      };
      setWbsTree((prev) => updateTree(prev));

      onWbsChange?.();
      toast.success("WBS updated");
    } catch (error) {
      console.error("Failed to update WBS:", error);
      toast.error("Failed to update WBS");
    } finally {
      setSavingWbs(false);
      setEditingWbsId(null);
      setEditingWbsName("");
    }
  };

  const handleCancelWbsEdit = () => {
    setEditingWbsId(null);
    setEditingWbsName("");
  };

  const handleDeleteWbs = async () => {
    if (!canEditStructure()) return; // defense-in-depth (backend: EDIT_STRUCTURE)
    if (!pendingDeleteWbs) return;
    setIsDeletingWbs(true);
    try {
      const { data } = await api.delete(`/wbs/${pendingDeleteWbs.id}`);
      toast.success(`"${pendingDeleteWbs.name}" deleted`);

      // Notify parent of doomed activities; parent filters allActivities,
      // derivation drops the empty WBS buckets from activitiesByWbs.
      if (data?.deleted_activity_ids?.length) {
        onActivitiesRemoved?.(data.deleted_activity_ids);
        setSelectedActivityIds((prev) => {
          const next = new Set(prev);
          for (const id of data.deleted_activity_ids) next.delete(id);
          return next.size === prev.size ? prev : next;
        });
      }

      // Merge cascade (surviving activities whose CPM dates moved).
      if (data?.cascaded?.length) {
        onActivitiesMerged?.(data.cascaded);
      }

      setPendingDeleteWbs(null);
      // Tree shape changed — refetch.
      onWbsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete WBS element");
    } finally {
      setIsDeletingWbs(false);
    }
  };

  // ============ Relationship Management ============

  // Fetch relationships for schedule
  const fetchRelationships = useCallback(async () => {
    if (!activeSchedule?.id) return;

    setLoadingRelationships(true);
    try {
      const response = await api.get(
        `/cpm/relationships/schedule/${activeSchedule.id}`,
      );
      setRelationships(response.data);
    } catch (error) {
      console.error("Failed to fetch relationships:", error);
    } finally {
      setLoadingRelationships(false);
    }
  }, [activeSchedule?.id]);

  // Load relationships when schedule changes
  useEffect(() => {
    if (activeSchedule?.id) {
      fetchRelationships();
    }
  }, [activeSchedule?.id, fetchRelationships]);

  // Get predecessors for an activity
  const getPredecessors = useCallback(
    (activityId) => {
      return relationships.filter((r) => r.successor_id === activityId);
    },
    [relationships],
  );

  // Get successors for an activity
  const getSuccessors = useCallback(
    (activityId) => {
      return relationships.filter((r) => r.predecessor_id === activityId);
    },
    [relationships],
  );

  // Get all activities for selection (flattened list)
  const getAllActivities = useMemo(() => {
    const activities = [];
    Object.values(activitiesByWbs).forEach((wbsActivities) => {
      activities.push(...wbsActivities);
    });
    return activities;
  }, [activitiesByWbs]);

  // Flat {id -> activity} lookup so the drag-end handler can resolve
  // active/over rows in O(1) without re-walking the WBS map.
  const activitiesById = useMemo(() => {
    const m = {};
    Object.values(activitiesByWbs).forEach((bucket) => {
      bucket.forEach((a) => {
        m[a.id] = a;
      });
    });
    return m;
  }, [activitiesByWbs]);

  // Flat sortable items in tree-render order. ONE SortableContext at the
  // DndContext level (not per-WBS) so the KeyboardSensor's arrow nav can
  // cross WBS boundaries — the cross-WBS reorder test depends on this.
  const sortableItemIds = useMemo(() => {
    const ids = [];
    const walk = (nodes) => {
      for (const node of nodes) {
        (activitiesByWbs[node.id] || []).forEach((a) => ids.push(a.id));
        if (node.children) walk(node.children);
      }
    };
    walk(wbsTree);
    return ids;
  }, [wbsTree, activitiesByWbs]);

  // Custom keyboard coordinate getter — `active` arrives as the id string.
  // Stock `sortableKeyboardCoordinates` chooses the next item via layout
  // rects, which JSDOM returns as all-zero (no layout engine) — arrow-key
  // nav never advances. We navigate by SortableContext items array and
  // synthesize a Y offset so closestCenter at the DndContext re-evaluates.
  const dndCoordinateGetter = useCallback((event, args) => {
    if (event.code !== "ArrowUp" && event.code !== "ArrowDown") {
      return sortableKeyboardCoordinates(event, args);
    }
    const { active, context, currentCoordinates } = args;
    const { droppableContainers, over } = context;
    const activeId = typeof active === "string" ? active : active?.id;
    if (!activeId) return sortableKeyboardCoordinates(event, args);
    const activeContainer = droppableContainers.get(activeId);
    const sortableData = activeContainer?.data?.current?.sortable;
    if (!sortableData) return sortableKeyboardCoordinates(event, args);
    const items = sortableData.items;
    // Advance from the CURRENT `over` position (not active's static
    // index) so successive ArrowUps walk up the list one row at a time.
    const overId = over?.id ?? activeId;
    const cursorIdx = items.indexOf(overId);
    const dir = event.code === "ArrowDown" ? 1 : -1;
    const nextIdx = cursorIdx + dir;
    if (nextIdx < 0 || nextIdx >= items.length) return undefined;
    event.preventDefault();
    // Synthetic droppable rects (above) are `top = idx * ROW_HEIGHT`.
    // The center-Y of `nextIdx` is `nextIdx * ROW_HEIGHT + ROW_HEIGHT / 2`.
    // Placing the collisionRect there makes closestCenter pick the
    // expected row unambiguously even with all-zero native rects.
    const targetY = nextIdx * ROW_HEIGHT + ROW_HEIGHT / 2;
    return { x: currentCoordinates.x, y: targetY };
  }, []);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: dndCoordinateGetter }),
  );

  // Custom droppable rect measurement. JSDOM returns all-zero rects for
  // every element (no layout engine), which means collision detection ties
  // all droppables at distance 0 and `getFirstCollision` always picks the
  // first registered — keyboard arrow-nav never actually advances `over`.
  // To make the unit tests deterministic AND mirror the real-list layout
  // (uniform-height rows in document order), we synthesize a per-row rect
  // whose `top` is its index in `sortableItemIds` times the row height.
  // In a real browser the native `getBoundingClientRect` already returns
  // non-zero values, so we fall back to the native measurement when the
  // element has a real width or height — preserving production behavior.
  const itemIndexById = useMemo(() => {
    const m = new Map();
    sortableItemIds.forEach((id, i) => m.set(id, i));
    return m;
  }, [sortableItemIds]);
  const dndMeasuring = useMemo(
    () => ({
      droppable: {
        // Use dnd-kit's `getClientRect` with `ignoreTransform: true`. This is
        // CRITICAL for closestCenter correctness during a drag: useSortable
        // applies `transform: translateY(...)` to non-active rows as the user
        // drags, and a naked `element.getBoundingClientRect()` returns the
        // POST-transform rect. closestCenter then compares the pointer to
        // shifted centers and resolves `over` to a row that isn't where the
        // user visually points. dnd-kit's own defaults use this transform-
        // agnostic rect (core.esm.js:646 `getTransformAgnosticClientRect`);
        // our prior override silently regressed it. Symptom that surfaced:
        // downward drag of the top row in a 60-row schedule "lands" on a
        // sibling several rows off from the visual target. Fix verified via
        // dnd-kit Issue #900 / Disc #1067 + core.esm.js source lines 595-636.
        measure: (element) => {
          const rect = getClientRect(element, { ignoreTransform: true });
          if (rect.width || rect.height) return rect;
          // Fallback: JSDOM-only (no layout engine → all-zero rects).
          // Synthesize a uniform-row-height rect from the row's position
          // in `sortableItemIds`. In a real browser the rect has non-zero
          // dimensions and is returned as-is.
          const id = element.getAttribute("data-activity-id");
          const idx = id != null ? itemIndexById.get(id) : undefined;
          if (idx == null) return rect;
          const top = idx * ROW_HEIGHT;
          return {
            top,
            bottom: top + ROW_HEIGHT,
            left: 0,
            right: 0,
            width: 0,
            height: ROW_HEIGHT,
            x: 0,
            y: top,
          };
        },
      },
    }),
    [itemIndexById],
  );

  // ── Collision detection: exclude active row from candidates ─────────
  //
  // The user's headline complaint ("top to down: nothing happens") was
  // dnd-kit's `closestCenter` returning the ACTIVE row as the closest
  // droppable on every downward drag. `useSortable` applies
  // `transform: translateY(deltaY)` to the active row so it follows
  // the pointer; the row's `getBoundingClientRect()` reflects the
  // transform, so its center IS the pointer's y. The active row wins
  // the "closest center" competition on every drag where it's still
  // a candidate. `handleReorder`'s first early-return then silently
  // swallows the drop (`if (active.id === over.id) return`).
  //
  // The fix is the documented dnd-kit pattern (Issue #900, Disc #1067):
  // filter `active.id` out of `droppableContainers` before delegating
  // to `closestCenter`. Symmetric drag behavior in both directions; no
  // change to cross-WBS resolution (geometry-only) or keyboard sensor
  // (same code path). Verified against @dnd-kit/core source.
  //
  // Plan reference: twinkling-squishing-lobster.md ("Top-to-down
  // silently swallowed" diagnosis).
  const reorderCollisionDetection = useCallback((args) => {
    const filtered = {
      ...args,
      droppableContainers: args.droppableContainers.filter(
        (c) => c.id !== args.active.id,
      ),
    };
    return closestCenter(filtered);
  }, []);

  // ── Reorder concurrency: commit barrier + explicit feedback ─────────
  //
  // The reorder is a non-commutative wide write — one drag mutates
  // display_order, optionally wbs_id, optionally a FS edge, AND runs
  // CPM producing dates across 10-50 cascaded rows. The systems with
  // this write shape (P6, MSProject, Smartsheet, Asana Timeline) all
  // pessimistic-commit. The classic anomaly the optimistic-merge
  // pattern produces here is lost-update / write-skew — a second drag
  // started before the first cascade lands merges against stale dates.
  //
  // Mental model: position is mine, schedule belongs to the engine.
  //   cascadePendingFor (state): id of the row being committed, drives
  //     UI (spinner on its handle; dim+disable other rows; cursor:wait).
  //   cascadePendingRef (ref): synchronous mirror so handleReorder's
  //     early-return sees the latest value across React's event tick.
  //   seqRef: per-activity monotonic client_seq for server idempotency.
  //
  // Plan reference: twinkling-squishing-lobster.md.
  const [cascadePendingFor, setCascadePendingFor] = useState(null);
  const cascadePendingRef = useRef(null);
  const seqRef = useRef({});

  // Hydrate seqRef from the server's persisted last_reorder_seq on every
  // activities refresh. Without this, the per-page-load counter starts
  // at 1 and the first POST against any row reordered in a prior session
  // 409s on the server (server keeps last_reorder_seq across sessions).
  // The user observed this as "drag does nothing" — the toast either
  // dismissed before they noticed, or they read the snap-back as a
  // broken feature. `max(local, server)` defends against a race where
  // `fetchData()` returns a snapshot taken before our in-flight commit
  // landed (don't let a stale server value clobber a higher local one).
  useEffect(() => {
    if (!Array.isArray(activities)) return;
    for (const a of activities) {
      if (!a?.id) continue;
      const serverSeq =
        typeof a.last_reorder_seq === "number" ? a.last_reorder_seq : 0;
      const localSeq = seqRef.current[a.id] || 0;
      if (serverSeq > localSeq) {
        seqRef.current[a.id] = serverSeq;
      }
    }
  }, [activities]);

  // Cross-WBS row drag opens a confirmation dialog instead of
  // silently no-opping (the prior UX bug: user dragged across WBS,
  // got no feedback, thought the feature was broken). Confirm fires
  // commitReorder with allow_cross_wbs=true; Cancel discards.
  const [pendingCrossWbs, setPendingCrossWbs] = useState(null);
  // Single-shot guard for the Move button. Radix's AlertDialogAction
  // composes the click through Slot, running our onClick once per
  // nested handler — observed 4× per click. This ref blocks re-entry
  // within the same tick. Reset when commitReorder's finally fires
  // (the activity is no longer "pending") or on Cancel.
  const confirmingCrossWbsRef = useRef(false);

  // Single POST site for /reorder. Same shape for same-WBS (called from
  // handleReorder) and cross-WBS (called from confirmCrossWbsMove).
  const commitReorder = useCallback(
    async ({
      activityId,
      activityCode,
      target_wbs_id,
      before_activity_id,
      after_activity_id,
      allow_cross_wbs,
      snapshot,
      nextOptimisticState,
    }) => {
      // Third-line defense against multi-fire: if a commit is already
      // in flight for this row, drop the redundant call. Belt-and-braces
      // alongside the lastDispatchedDragRef guard in handleReorder.
      if (cascadePendingRef.current === activityId) return;

      const nextSeq = (seqRef.current[activityId] || 0) + 1;
      seqRef.current[activityId] = nextSeq;

      cascadePendingRef.current = activityId;
      setCascadePendingFor(activityId);
      onActivitiesOptimistic?.(() => nextOptimisticState);

      try {
        const res = await api.post(`/activities/${activityId}/reorder`, {
          target_wbs_id,
          before_activity_id,
          after_activity_id,
          create_fs_relationship: true,
          allow_cross_wbs: Boolean(allow_cross_wbs),
          client_seq: nextSeq,
        });
        const { activity: serverActivity, cascaded } =
          parseActivityUpdateResponse(res.data);
        const updates = [serverActivity, ...cascaded].filter((a) => a && a.id);
        if (updates.length) {
          onActivitiesMerged?.(updates);
        }
        // Success feedback — closes the loop the user couldn't see before.
        toast.success(`Reordered ${activityCode || activityId.slice(0, 6)}`);
      } catch (err) {
        onActivitiesOptimistic?.(() => snapshot);
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 409) {
          toast.error(detail || "Reorder rejected — refreshing schedule");
          // Refetch activities and merge them into parent state so seqRef
          // re-hydrates from the server's current last_reorder_seq. Without
          // this, a stale seqRef keeps generating new requests that all 409
          // and the schedule appears frozen. `fetchData` only refreshes
          // WBS/resources/calendars (parent owns activities), so we must
          // hit the activities endpoint directly and pipe through
          // onActivitiesMerged.
          try {
            if (activeSchedule?.id) {
              const resp = await api.get(
                `/activities/schedule/${activeSchedule.id}`,
              );
              const fresh = Array.isArray(resp.data) ? resp.data : [];
              if (fresh.length && onActivitiesMerged) {
                onActivitiesMerged(fresh);
              }
            }
            await fetchData();
          } catch (_) {
            /* silent — rollback already converged UI */
          }
        } else {
          toast.error(detail || "Reorder failed");
        }
      } finally {
        cascadePendingRef.current = null;
        setCascadePendingFor(null);
      }
    },
    [onActivitiesOptimistic, onActivitiesMerged, fetchData, activeSchedule?.id],
  );

  // Tracks the activityId of the most recently dispatched onDragEnd so
  // we can short-circuit a duplicate dispatch from the same gesture.
  // dnd-kit (or React StrictMode, or a parent re-render mid-drag) was
  // observed firing onDragEnd 2-4 times for one user drop — sometimes
  // synchronously, sometimes spaced across multiple microtasks because
  // the second dispatch was queued from a sensor cleanup phase that
  // runs after the first dispatch's commit cycle has already settled.
  // A short setTimeout(0) cleanup is NOT enough: the await inside
  // commitReorder flushes the microtask queue, the catch/finally
  // clears cascadePendingRef, and only then does the duplicate
  // dispatch arrive — finding both refs null.
  //
  // Window of 1000ms is far longer than any sensible drag-end dispatch
  // delay (dnd-kit's longest internal scheduling is one animation
  // frame, ~16ms). A real user cannot start a second drag of the same
  // row within 1000ms anyway; they'd be in the spinner state from the
  // commit barrier. If they somehow can (e.g., after a 400/409 that
  // resolves instantly), this guard delays the second gesture by at
  // most the remainder of 1000ms.
  const lastDispatchedDragRef = useRef(null);
  const lastDispatchedDragTimeoutRef = useRef(null);

  const handleReorder = useCallback(
    (event) => {
      // Reordering rows / moving an activity across WBS is a STRUCTURAL change
      // (EDIT_STRUCTURE, plan-level). Guard at the handler so view/collaborate
      // members can never trigger an optimistic reorder the backend will 403 —
      // honoring the hide/disable contract. The drag handle is also hidden below
      // for non-editors; this is defense-in-depth (keyboard sensor, etc.).
      if (!canEditStructure()) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      // Synchronous truth — state batching defeats closure-read.
      if (cascadePendingRef.current) return;
      // Duplicate-dispatch guard: same gesture fired onDragEnd already.
      if (lastDispatchedDragRef.current === active.id) return;
      lastDispatchedDragRef.current = active.id;
      if (lastDispatchedDragTimeoutRef.current)
        clearTimeout(lastDispatchedDragTimeoutRef.current);
      lastDispatchedDragTimeoutRef.current = setTimeout(() => {
        if (lastDispatchedDragRef.current === active.id) {
          lastDispatchedDragRef.current = null;
        }
        lastDispatchedDragTimeoutRef.current = null;
      }, 1000);

      const moved = activitiesById[active.id];
      const overActivity = activitiesById[over.id];
      if (!moved || !overActivity) return;

      const snapshot = activities;
      const isCrossWbs = moved.wbs_id !== overActivity.wbs_id;
      const target_wbs_id = overActivity.wbs_id;

      const destBucket = activitiesByWbs[target_wbs_id] || [];
      const overIndex = destBucket.findIndex((a) => a.id === over.id);
      let nextDestBucket;
      if (isCrossWbs) {
        // Splice the moved row into the destination bucket at the over
        // index; remove it from the source bucket below.
        nextDestBucket = [
          ...destBucket.slice(0, overIndex),
          { ...moved, wbs_id: target_wbs_id },
          ...destBucket.slice(overIndex),
        ];
      } else {
        const fromIndex = destBucket.findIndex((a) => a.id === active.id);
        if (fromIndex < 0 || overIndex < 0 || fromIndex === overIndex) return;
        nextDestBucket = arrayMove(destBucket, fromIndex, overIndex);
      }

      const idx = nextDestBucket.findIndex((a) => a.id === active.id);
      const before_activity_id = idx > 0 ? nextDestBucket[idx - 1].id : null;
      const after_activity_id =
        idx < nextDestBucket.length - 1 ? nextDestBucket[idx + 1].id : null;

      // Flatten back to the parent's flat-list shape. Same-WBS: replace
      // that bucket only. Cross-WBS: also strip the moved row from its
      // source bucket.
      const nextOptimisticState = isCrossWbs
        ? [
            ...snapshot.filter(
              (a) => a.id !== active.id && a.wbs_id !== target_wbs_id,
            ),
            ...nextDestBucket,
          ]
        : [
            ...snapshot.filter((a) => a.wbs_id !== target_wbs_id),
            ...nextDestBucket,
          ];

      if (isCrossWbs) {
        // Stage the move; dialog confirms or cancels. No POST yet.
        setPendingCrossWbs({
          activityId: active.id,
          activityCode: moved.activity_id,
          moved,
          sourceWbsId: moved.wbs_id,
          targetWbsId: target_wbs_id,
          before_activity_id,
          after_activity_id,
          snapshot,
          nextOptimisticState,
        });
        return;
      }

      commitReorder({
        activityId: active.id,
        activityCode: moved.activity_id,
        target_wbs_id,
        before_activity_id,
        after_activity_id,
        allow_cross_wbs: false,
        snapshot,
        nextOptimisticState,
      });
    },
    [
      activitiesById,
      activitiesByWbs,
      activities,
      commitReorder,
      canEditStructure,
    ],
  );

  const confirmCrossWbsMove = useCallback(() => {
    // Single-shot guard: Radix's <AlertDialogAction> composes onClick
    // through Slot, which could fan out the click into multiple
    // handler invocations. The ref blocks re-entry within the same
    // tick. Reset after a macrotask in the success path or
    // immediately in the cancel path.
    if (confirmingCrossWbsRef.current) return;
    confirmingCrossWbsRef.current = true;
    if (!pendingCrossWbs) {
      confirmingCrossWbsRef.current = false;
      return;
    }
    const p = pendingCrossWbs;
    setPendingCrossWbs(null);
    commitReorder({
      activityId: p.activityId,
      activityCode: p.activityCode,
      target_wbs_id: p.targetWbsId,
      before_activity_id: p.before_activity_id,
      after_activity_id: p.after_activity_id,
      allow_cross_wbs: true,
      snapshot: p.snapshot,
      nextOptimisticState: p.nextOptimisticState,
    });
    // Reset the single-shot guard once the dialog has fully closed.
    // Use a macrotask hop so Radix's nested onClick chain finishes
    // its synchronous fan-out before we re-arm the handler for the
    // next legitimate cross-WBS gesture.
    setTimeout(() => {
      confirmingCrossWbsRef.current = false;
    }, 500);
  }, [pendingCrossWbs, commitReorder]);

  const cancelCrossWbsMove = useCallback(() => {
    setPendingCrossWbs(null);
    confirmingCrossWbsRef.current = false;
  }, []);

  // Helper for the cross-WBS dialog body — walks the WBS tree to look
  // up display names for the source/target nodes.
  const findWbsNode = useCallback(
    (id) => {
      if (!id) return null;
      const walk = (nodes) => {
        for (const n of nodes) {
          if (n.id === id) return n;
          if (n.children) {
            const f = walk(n.children);
            if (f) return f;
          }
        }
        return null;
      };
      return walk(wbsTree);
    },
    [wbsTree],
  );

  // Get the currently selected activity object
  const selectedActivity = useMemo(() => {
    if (!selectedActivityId) return null;
    return getAllActivities.find((a) => a.id === selectedActivityId);
  }, [selectedActivityId, getAllActivities]);

  // ------------------------------------------------------------------
  // Activity clipboard — Ctrl+C copies selected rows as Excel-friendly
  // TSV+HTML, Ctrl+V parses Excel TSV into new activities.
  //
  // Listens on `document` because copy/paste events bubble from the focus
  // target. The handlers self-gate: skip when focus is in an input/textarea
  // (preserve native cell-editor copy/paste), skip when no activity rows
  // are selected (let WBS copy or browser default handle it).
  // ------------------------------------------------------------------
  useEffect(() => {
    const isEditableTarget = (target) => {
      if (!target) return false;
      const tag = (target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select")
        return true;
      if (target.isContentEditable) return true;
      return false;
    };

    const handleCopy = (e) => {
      if (isEditableTarget(e.target)) return;
      if (!selectedActivityIds || selectedActivityIds.size === 0) return;

      // Visible columns in canonical CLIPBOARD_COLUMNS order.
      const visibleKeys = ACTIVITY_CLIPBOARD_COLUMNS.map((c) => c.key).filter(
        (key) => isColumnVisible(key),
      );

      // Selection-order preservation: iterate getAllActivities, keep those
      // in the selection Set. This matches how the user sees them on screen.
      const selected = getAllActivities.filter((a) =>
        selectedActivityIds.has(a.id),
      );
      if (selected.length === 0) return;

      const tsv = serializeActivitiesToTSV(selected, visibleKeys);
      const html = serializeActivitiesToHTML(selected, visibleKeys);

      try {
        e.clipboardData.setData("text/plain", tsv);
        e.clipboardData.setData("text/html", html);
        e.preventDefault();
        toast.success(
          `Copied ${selected.length} ${selected.length === 1 ? "activity" : "activities"} to clipboard`,
        );
      } catch (err) {
        // If setData throws (rare), fall through to browser default.
        console.error("Activity clipboard copy failed:", err);
      }
    };

    const handlePaste = (e) => {
      if (isEditableTarget(e.target)) return;
      // Pasting activity rows fires POST /activities (EDIT_ACTIVITY, plan-level).
      // Gate the listener so view/collaborate can't trigger the write path —
      // mirrors the WBS-paste gate in SchedulingScreen.
      if (!canEditActivities()) return;
      if (!onPasteActivities) return;
      // Defer to WBS paste flow if a WBS clipboard payload is queued —
      // SchedulingScreen's keydown handler runs first and preventDefaults
      // in that case, so we should never even see the event. This is
      // belt-and-braces.
      if (clipboardWbs) return;

      const text = e.clipboardData?.getData("text/plain") || "";
      if (!text.trim()) return;

      const rows = parseActivitiesTSV(text);
      if (rows.length === 0) return;

      e.preventDefault();
      Promise.resolve(onPasteActivities(rows)).catch((err) => {
        console.error("Activity clipboard paste failed:", err);
      });
    };

    document.addEventListener("copy", handleCopy);
    document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("paste", handlePaste);
    };
  }, [
    selectedActivityIds,
    getAllActivities,
    isColumnVisible,
    onPasteActivities,
    clipboardWbs,
    hiddenColumns,
    baselineComparisonData,
    baselineColumnsData,
    canEditActivities,
  ]);

  // Helper to get activity details by activity_id (e.g., "A1010")
  const getActivityByCode = useCallback(
    (activityCode) => {
      return getAllActivities.find((a) => a.activity_id === activityCode);
    },
    [getAllActivities],
  );

  // Open relationship dialog
  const handleOpenAddRelation = (activityId, type) => {
    setShowRelationshipsFor(activityId);
    setAddRelationType(type);
    setNewRelation({
      activityId: "",
      relationshipType: "FS",
      lagDays: 0,
    });
    setShowAddRelationDialog(true);
  };

  // Create new relationship
  const handleCreateRelationship = async () => {
    // Editing relationships is plan-level (EDIT_ACTIVITY). Guard at the handler
    // for parity with reorder/WBS — the trigger buttons are already gated, this
    // is defense-in-depth against any non-UI path.
    if (!canEditActivities()) return;
    if (!newRelation.activityId || !showRelationshipsFor) {
      toast.error("Please select an activity");
      return;
    }

    setSavingRelation(true);
    try {
      const requestData = {
        schedule_id: activeSchedule.id,
        predecessor_id:
          addRelationType === "predecessor"
            ? newRelation.activityId
            : showRelationshipsFor,
        successor_id:
          addRelationType === "predecessor"
            ? showRelationshipsFor
            : newRelation.activityId,
        relationship_type: newRelation.relationshipType,
        lag_days: parseInt(newRelation.lagDays) || 0,
      };

      // Backend returns RelationshipMutationResponse with the new
      // relationship AND every activity whose dates moved as a result
      // (plan zesty-coalescing-clarke.md). Merge the cascaded activities
      // into local state in the same tick — no re-fetch round-trip needed.
      const { data } = await api.post("/cpm/relationships", requestData);

      toast.success("Relationship created");
      setShowAddRelationDialog(false);
      await fetchRelationships();

      if (data?.cascaded?.length) {
        // Merge cascade into parent allActivities; derivation propagates
        // to activitiesByWbs in the same tick.
        if (onActivitiesMerged) onActivitiesMerged(data.cascaded);
        else onActivityChange?.();
      } else {
        // Defensive: if the backend didn't return cascade (e.g. CPM
        // failed), fall back to the big-hammer refresh so we don't leave
        // stale dates on screen.
        onActivityChange?.();
      }
    } catch (error) {
      console.error("Failed to create relationship:", error);
      toast.error(
        error.response?.data?.detail || "Failed to create relationship",
      );
    } finally {
      setSavingRelation(false);
    }
  };

  // Delete relationship
  const handleDeleteRelationship = async (relationshipId) => {
    // Plan-level (EDIT_ACTIVITY) — defense-in-depth guard for parity with the
    // create handler and the gated trigger buttons.
    if (!canEditActivities()) return;
    try {
      // DELETE /cpm/relationships/{id} now returns 200 with
      // RelationshipMutationResponse (was 204). Merge the cascaded
      // activities so the former-successor's dates update in place.
      const { data } = await api.delete(`/cpm/relationships/${relationshipId}`);
      toast.success("Relationship removed");
      await fetchRelationships();

      if (data?.cascaded?.length) {
        if (onActivitiesMerged) onActivitiesMerged(data.cascaded);
        else onActivityChange?.();
      } else {
        onActivityChange?.();
      }
    } catch (error) {
      console.error("Failed to delete relationship:", error);
      toast.error("Failed to delete relationship");
    }
  };

  // Toggle showing relationships for an activity
  const toggleShowRelationships = (activityId, e) => {
    e?.stopPropagation();
    setShowRelationshipsFor((prev) =>
      prev === activityId ? null : activityId,
    );
  };

  // ============ Progress Tracking (MODULE O-1) ============

  // Open progress update panel for an activity
  const handleOpenProgressUpdate = (activity, e) => {
    // This panel edits ONLY % Complete + Remaining Duration — full-schedule
    // fields that are owner-only (EDIT_ACTIVITY), not the Collaborator
    // "actuals only" surface (Roles_and_Permissions_Spec §7). Actual Start /
    // Finish are edited from their own inline columns (canEditActuals).
    // Bail BEFORE swallowing the event: a non-owner's click must still bubble
    // up to select the row instead of being silently consumed.
    if (!canEditActivities()) return;
    e?.stopPropagation();

    setEditingProgressFor(activity.id);
    setProgressUpdate({
      actual_start: activity.actual_start?.split("T")[0] || null,
      actual_finish: activity.actual_finish?.split("T")[0] || null,
      percent_complete: activity.percent_complete || 0,
      remaining_duration:
        activity.remaining_duration || activity.original_duration || 0,
    });
    // Clear any stale validation error from a previous edit session.
    setProgressUpdateError(null);
  };

  // Save progress update
  const handleSaveProgress = async (activity) => {
    if (!canEditActivities()) return; // owner-only: %Complete/Remaining require EDIT_ACTIVITY (backend /progress field-gate)
    setSavingProgress(true);
    try {
      const updateData = {};

      // Actual Start / Actual Finish are NOT edited from this panel anymore —
      // they have dedicated inline-editable columns. This panel only updates
      // % Complete and Remaining Duration.
      if (progressUpdate.percent_complete !== undefined) {
        updateData.percent_complete = progressUpdate.percent_complete;
      }
      if (progressUpdate.remaining_duration !== undefined) {
        updateData.remaining_duration = progressUpdate.remaining_duration;
      }

      const response = await api.put(
        `/activities/${activity.id}/progress`,
        updateData,
      );
      // Response is ActivityUpdateResponse: { activity, cascaded, cpm_ran }.
      // Use the server's authoritative copy so backend-derived fields land
      // in the UI without a refetch: status auto-flip (e.g. "completed" at
      // 100%), remaining_duration snapping on actual_finish, and any CPM-
      // driven date shifts on downstream activities arriving via `cascaded`.
      const { activity: serverActivity, cascaded } =
        parseActivityUpdateResponse(response.data);

      // Push to undo stack for percent_complete changes
      if (setUndoStack && progressUpdate.percent_complete !== undefined) {
        setUndoStack((prev) => {
          const newEntry = {
            id: Date.now(),
            activityId: activity.id,
            activityName: activity.name,
            field: "percent_complete",
            displayName: "Progress",
            oldValue: activity.percent_complete || 0,
            newValue: progressUpdate.percent_complete,
            timestamp: new Date().toISOString(),
          };
          const newStack = [newEntry, ...prev];
          return newStack.slice(0, 20);
        });
      }

      // Merge the server activity AND any cascaded rows in a single round-trip.
      const allUpdates = [serverActivity, ...cascaded];
      if (onActivitiesMerged) onActivitiesMerged(allUpdates);

      toast.success("Progress updated");
      setEditingProgressFor(null);
      setEditComment("");
      onActivityChange?.();
    } catch (error) {
      console.error("Failed to update progress:", error);
      toast.error(error.response?.data?.detail || "Failed to update progress");
    } finally {
      setSavingProgress(false);
    }
  };

  // Auto-calculate remaining duration based on % complete
  const calculateRemainingFromPercent = (activity, percent) => {
    const originalDuration = activity.original_duration || 1;
    const remaining = Math.round((originalDuration * (100 - percent)) / 100);
    return Math.max(0, remaining);
  };

  // ============ Calendar Editing ============

  // Open calendar edit panel for an activity
  const handleOpenCalendarEdit = (activity, e) => {
    // Bail BEFORE swallowing the event so a user who cannot edit activities
    // still gets normal row selection on the click.
    if (!canEditActivities()) return;
    e?.stopPropagation();

    if (editingCalendarFor === activity.id) {
      setEditingCalendarFor(null);
    } else {
      setEditingCalendarFor(activity.id);
      setSelectedCalendarId(activity.calendar_id || "");
    }
  };

  // Save calendar change
  const handleSaveCalendar = async (activity) => {
    if (!canEditActivities()) return; // defense-in-depth (backend: EDIT_ACTIVITY)
    setSavingCalendar(true);
    try {
      const updatePayload = {
        calendar_id: selectedCalendarId || null,
      };
      if (editComment.trim()) {
        updatePayload.comment = editComment.trim();
      }

      await api.put(`/activities/${activity.id}`, updatePayload);

      // Push to undo stack
      if (setUndoStack) {
        setUndoStack((prev) => {
          const newEntry = {
            id: Date.now(),
            activityId: activity.id,
            activityName: activity.name,
            field: "calendar_id",
            displayName: "Calendar",
            oldValue: activity.calendar_id || null,
            newValue: selectedCalendarId || null,
            timestamp: new Date().toISOString(),
          };
          const newStack = [newEntry, ...prev];
          return newStack.slice(0, 20);
        });
      }

      // Merge calendar update via parent; derivation propagates.
      onActivitiesMerged?.([
        {
          ...activity,
          calendar_id: selectedCalendarId || null,
        },
      ]);

      toast.success("Calendar updated");
      setEditingCalendarFor(null);
      setEditComment("");
      onActivityChange?.();
    } catch (error) {
      console.error("Failed to update calendar:", error);
      toast.error(error.response?.data?.detail || "Failed to update calendar");
    } finally {
      setSavingCalendar(false);
    }
  };

  // Get calendar name for display
  const getCalendarName = (calendarId) => {
    if (!calendarId) return "Project Default";
    const cal = projectCalendars.find((c) => c.id === calendarId);
    return cal ? cal.name : "Unknown Calendar";
  };

  // ============ Resource Management (MODULE O-2) ============

  // Toggle showing resources for an activity
  const toggleShowResources = (activityId, e) => {
    e?.stopPropagation();
    setShowResourcesFor((prev) => (prev === activityId ? null : activityId));
  };

  // Get resources for an activity
  const getActivityResourceAssignments = useCallback(
    (activityId) => {
      return activityResources[activityId] || [];
    },
    [activityResources],
  );

  // Assign resource to activity
  const handleAssignResource = async (activityId) => {
    if (!canEditStructure()) return; // defense-in-depth (backend: EDIT_STRUCTURE)
    if (!newResourceAssignment.resource_id) {
      toast.error("Please select a resource");
      return;
    }

    setSavingResource(true);
    try {
      const response = await api.post(
        `/resources/activity/${activityId}/assign`,
        {
          resource_id: newResourceAssignment.resource_id,
          units: newResourceAssignment.units,
        },
      );

      // Update local state
      setActivityResources((prev) => ({
        ...prev,
        [activityId]: [...(prev[activityId] || []), response.data],
      }));

      toast.success("Resource assigned");
      setShowAddResourceDialog(false);
      setNewResourceAssignment({ resource_id: "", units: 1 });
    } catch (error) {
      console.error("Failed to assign resource:", error);
      toast.error(error.response?.data?.detail || "Failed to assign resource");
    } finally {
      setSavingResource(false);
    }
  };

  // Remove resource from activity
  const handleRemoveResource = async (activityId, assignmentId) => {
    if (!canEditStructure()) return; // defense-in-depth (backend: EDIT_STRUCTURE)
    try {
      await api.delete(
        `/resources/activity/${activityId}/assignments/${assignmentId}`,
      );

      // Update local state
      setActivityResources((prev) => ({
        ...prev,
        [activityId]: (prev[activityId] || []).filter(
          (a) => a.id !== assignmentId,
        ),
      }));

      toast.success("Resource removed");
    } catch (error) {
      console.error("Failed to remove resource:", error);
      toast.error("Failed to remove resource");
    }
  };

  // Create new resource
  const handleCreateResource = async () => {
    if (!canEditStructure()) return; // defense-in-depth (backend: EDIT_STRUCTURE)
    if (!newResource.name.trim()) {
      toast.error("Resource name is required");
      return;
    }

    setSavingResource(true);
    try {
      const response = await api.post("/resources", {
        name: newResource.name,
        resource_type: newResource.resource_type,
        unit: newResource.unit || null,
        schedule_id: activeSchedule.id,
      });

      setResources((prev) => [...prev, response.data]);
      toast.success("Resource created");
      setShowCreateResourceDialog(false);
      setNewResource({ name: "", resource_type: "person", unit: "" });
    } catch (error) {
      console.error("Failed to create resource:", error);
      toast.error(error.response?.data?.detail || "Failed to create resource");
    } finally {
      setSavingResource(false);
    }
  };

  // Get resource type icon - returns proper icon based on resource type
  const getResourceTypeIcon = (type) => {
    const typeLower = type?.toLowerCase();
    switch (typeLower) {
      case "person":
      case "labor":
        return Users;
      case "equipment":
        return Wrench;
      case "material":
        return Package;
      default:
        // Default to Equipment for unknown types (including legacy 'vehicle')
        return Wrench;
    }
  };

  // Filter tree based on search
  const filterTree = useCallback(
    (nodes, query) => {
      if (!query) return nodes;

      const lowerQuery = query.toLowerCase();

      const filterNode = (node) => {
        const nodeMatches =
          node.name.toLowerCase().includes(lowerQuery) ||
          node.code.toLowerCase().includes(lowerQuery);

        // Check if any activities in this WBS match
        const nodeActivities = activitiesByWbs[node.id] || [];
        const activityMatches = nodeActivities.some(
          (a) =>
            a.name.toLowerCase().includes(lowerQuery) ||
            a.activity_id.toLowerCase().includes(lowerQuery),
        );

        const filteredChildren = node.children
          ? node.children.map(filterNode).filter(Boolean)
          : [];

        if (nodeMatches || activityMatches || filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }
        return null;
      };

      return nodes.map(filterNode).filter(Boolean);
    },
    [activitiesByWbs],
  );

  // Flat lookup: wbsId -> { name, code } for WBS-name-match bypass in activity filter
  const wbsLookup = useMemo(() => {
    const map = {};
    const walk = (nodes) => {
      for (const node of nodes) {
        map[node.id] = { name: node.name, code: node.code };
        if (node.children) walk(node.children);
      }
    };
    walk(wbsTree);
    return map;
  }, [wbsTree]);

  // Apply all filters (look-ahead, status, date range) - VISIBILITY ONLY, no data mutation
  const filteredActivitiesByWbs = useMemo(() => {
    if (
      !lookAheadWeeks &&
      statusFilter === "all" &&
      !dateRangeFilter.from &&
      !dateRangeFilter.to &&
      !activitySearch.trim() &&
      !criticalPathOnly
    ) {
      return activitiesByWbs;
    }

    const today = new Date();
    const lookAheadEndDate = lookAheadWeeks
      ? addWeeks(today, lookAheadWeeks)
      : null;

    const filtered = {};
    Object.entries(activitiesByWbs).forEach(([wbsId, activities]) => {
      filtered[wbsId] = activities.filter((activity) => {
        // Status filter
        if (statusFilter !== "all" && activity.status !== statusFilter) {
          return false;
        }

        // Look-ahead filter — uses canonical priority so in-progress
        // activities aren't accidentally hidden by an outdated early_start.
        if (lookAheadWeeks) {
          const activityStart = selectActivityStart(activity);
          if (activityStart) {
            const startDate = parseISO(activityStart.split("T")[0]);
            // Show activities that start within the look-ahead window
            if (isAfter(startDate, lookAheadEndDate)) {
              return false;
            }
          }
        }

        // Date range filter
        if (dateRangeFilter.from || dateRangeFilter.to) {
          const activityStart = selectActivityStart(activity);
          const activityFinish = selectActivityFinish(activity);

          if (activityStart && activityFinish) {
            const startDate = parseISO(activityStart.split("T")[0]);
            const finishDate = parseISO(activityFinish.split("T")[0]);

            // Check if activity falls within or overlaps the date range
            if (dateRangeFilter.from && dateRangeFilter.to) {
              // Activity must overlap with date range
              if (
                isAfter(startDate, dateRangeFilter.to) ||
                isBefore(finishDate, dateRangeFilter.from)
              ) {
                return false;
              }
            } else if (dateRangeFilter.from) {
              // Activity must finish on or after the from date
              if (isBefore(finishDate, dateRangeFilter.from)) {
                return false;
              }
            } else if (dateRangeFilter.to) {
              // Activity must start on or before the to date
              if (isAfter(startDate, dateRangeFilter.to)) {
                return false;
              }
            }
          }
        }

        // Activity ID / Name text search (skip if parent WBS matches the query)
        if (activitySearch.trim()) {
          const q = activitySearch.toLowerCase();
          const parentWbs = wbsLookup[wbsId];
          const wbsMatches =
            parentWbs &&
            (parentWbs.name.toLowerCase().includes(q) ||
              parentWbs.code.toLowerCase().includes(q));
          if (!wbsMatches) {
            if (
              !activity.activity_id?.toLowerCase().includes(q) &&
              !activity.name?.toLowerCase().includes(q)
            )
              return false;
          }
        }

        // Critical path only
        if (criticalPathOnly && !activity.is_critical) return false;

        return true;
      });
    });

    return filtered;
  }, [
    activitiesByWbs,
    lookAheadWeeks,
    statusFilter,
    dateRangeFilter,
    activitySearch,
    criticalPathOnly,
    wbsLookup,
  ]);

  const filteredTree = useMemo(
    () => filterTree(wbsTree, activitySearch),
    [wbsTree, activitySearch, filterTree],
  );

  // Expand/Collapse All
  const handleExpandAll = () => {
    const allExpanded = {};
    const expandAll = (nodes) => {
      nodes.forEach((node) => {
        allExpanded[node.id] = true;
        if (node.children) expandAll(node.children);
      });
    };
    expandAll(wbsTree);
    setExpandedNodes(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedNodes({});
  };

  // Format helpers — delegated to timezone-safe shared utility.
  const formatDate = formatDateShort;
  const formatFullDate = formatDateLong;

  // Determine effective status based on actual_finish and percent_complete
  const getEffectiveStatus = (activity) => {
    // If actual_finish exists OR percent_complete >= 100, it's completed
    if (
      activity.actual_finish ||
      (activity.percent_complete && activity.percent_complete >= 100)
    ) {
      return "completed";
    }
    // If actual_start exists OR percent_complete > 0, it's in progress
    if (
      activity.actual_start ||
      (activity.percent_complete && activity.percent_complete > 0)
    ) {
      return "in_progress";
    }
    return activity.status || "not_started";
  };

  // Get row text color class based on activity status
  const getStatusTextColorClass = (activity) => {
    const status = getEffectiveStatus(activity);
    switch (status) {
      case "completed":
        return "text-emerald-600"; // Green for completed
      case "in_progress":
        return "text-amber-600"; // Yellow/amber for in progress (pending)
      case "not_started":
      default:
        return "text-slate-600"; // Grey for not started (default)
    }
  };

  const getStatusBadge = (status, size = "small", activity = null) => {
    // Use effective status if activity is provided
    const effectiveStatus = activity ? getEffectiveStatus(activity) : status;

    const config = {
      not_started: {
        label: "Pending",
        className: "bg-slate-100 text-slate-600 border-slate-200",
        icon: Clock,
      },
      in_progress: {
        label: "In Progress",
        className: "bg-amber-50 text-amber-700 border-amber-200",
        icon: Loader2,
      },
      completed: {
        label: "Completed",
        className: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: CheckCircle2,
      },
    };
    const cfg = config[effectiveStatus] || config.not_started;

    if (size === "large") {
      const Icon = cfg.icon;
      return (
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${cfg.className}`}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="text-xs font-medium">{cfg.label}</span>
        </div>
      );
    }

    return (
      <Badge
        variant="outline"
        className={`text-[10px] px-1.5 py-0 h-5 font-medium ${cfg.className}`}
      >
        {cfg.label}
      </Badge>
    );
  };

  // Count nodes
  const countNodes = (nodes) => {
    let count = nodes.length;
    nodes.forEach((node) => {
      if (node.children) count += countNodes(node.children);
    });
    return count;
  };

  const totalActivities = Object.values(activitiesByWbs).flat().length;

  // WBS Creation
  // parentId: string UUID of the parent WBS (when asChild=true), or null for root level
  const handleOpenAddWbsDialog = (asChild = false, parentId = null) => {
    setAddWbsAsChild(asChild);
    setAddWbsParentId(asChild ? parentId : null);
    setNewWbs({ code: "", name: "", description: "" });
    setShowAddWbsDialog(true);
  };

  const handleCreateWbs = async (e) => {
    e.preventDefault();
    if (!canEditStructure()) return; // defense-in-depth (backend: EDIT_STRUCTURE)
    if (!newWbs.code.trim() || !newWbs.name.trim()) {
      toast.error("Code and Name are required");
      return;
    }

    setIsCreatingWbs(true);
    try {
      const payload = {
        code: newWbs.code.trim(),
        name: newWbs.name.trim(),
        description: newWbs.description?.trim() || null,
        schedule_id: activeSchedule.id,
        parent_id: addWbsAsChild ? addWbsParentId : null,
      };

      await api.post("/wbs", payload);
      toast.success("WBS element created");
      setShowAddWbsDialog(false);
      await fetchData();
      onWbsChange?.();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create WBS";
      toast.error(message);
    } finally {
      setIsCreatingWbs(false);
    }
  };

  // Activity Creation
  const handleOpenAddActivityDialog = (wbsId, wbsNode) => {
    setAddActivityToWbsId(wbsId);
    setNewActivity({
      name: "",
      original_duration: 1,
      planned_start: "",
      planned_finish: "",
      status: "not_started",
      calendar_id: "",
    });
    setShowAddActivityDialog(true);
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    if (!canEditActivities()) return; // defense-in-depth (backend: EDIT_ACTIVITY)
    if (!newActivity.name.trim()) {
      toast.error("Activity Name is required");
      return;
    }

    setIsCreatingActivity(true);
    try {
      const payload = {
        name: newActivity.name.trim(),
        schedule_id: activeSchedule.id,
        wbs_id: addActivityToWbsId,
        original_duration: parseInt(newActivity.original_duration) || 1,
        remaining_duration: parseInt(newActivity.original_duration) || 1,
        planned_start: newActivity.planned_start || null,
        planned_finish: newActivity.planned_finish || null,
        status: newActivity.status,
        calendar_id: newActivity.calendar_id || null,
      };

      await api.post("/activities", payload);
      toast.success("Activity created");
      setShowAddActivityDialog(false);
      await fetchData();
      onActivityChange?.();
    } catch (error) {
      const message =
        error.response?.data?.detail || "Failed to create activity";
      toast.error(message);
    } finally {
      setIsCreatingActivity(false);
    }
  };

  // Render WBS node with its activities nested beneath
  const renderNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.id];
    const isSelected = selectedWbsId === node.id;
    const nodeActivities = filteredActivitiesByWbs[node.id] || [];
    const hasActivities = nodeActivities.length > 0;
    const isTopLevel = level === 0;
    const isEditingWbs = editingWbsId === node.id;
    const rowHeight = isTopLevel ? WBS_ROW_HEIGHT_TOP : ROW_HEIGHT;

    return (
      <div key={node.id} className="select-none">
        {/* WBS Row - Table Structure */}
        <div
          className={`
            group flex items-center border-l-2 transition-colors cursor-pointer px-2 border-b border-slate-200
            ${
              isSelected
                ? "bg-amber-50 border-l-amber-500"
                : "border-l-transparent hover:bg-slate-50 hover:border-l-slate-300"
            }
          `}
          style={{ height: `${rowHeight}px`, minHeight: `${rowHeight}px` }}
          onClick={(e) => handleSelectWbs(node, e)}
          onContextMenu={(e) => {
            // Always open: the menu carries Comments for every role, plus
            // copy/paste when those handlers are provided.
            e.preventDefault();
            e.stopPropagation();
            setSelectedWbsId(node.id);
            onSelectWbs?.(node);
            setWbsContextMenu({ x: e.clientX, y: e.clientY, node });
          }}
          data-testid={`wbs-node-${node.id}`}
        >
          {/* Bulk-select - Empty placeholder. Position 1 so it aligns with the
              activity-row checkbox cell. Gated on edit perms — same gate as
              the body checkbox. */}
          {canEditActivities() && (
            <div
              className="flex items-center justify-center flex-shrink-0 border-r border-slate-300"
              style={{ width: "24px", minWidth: "24px", maxWidth: "24px" }}
              data-testid="bulk-select-wbs-placeholder"
            />
          )}

          {/* Links Column */}
          <div
            className="flex items-center justify-center border-r border-slate-300 flex-shrink-0"
            style={{
              width: `${columnWidths.links}px`,
              minWidth: `${columnWidths.links}px`,
            }}
          />

          {/* ID Column */}
          <div
            className="flex items-center px-2 border-r border-slate-300 flex-shrink-0"
            style={{
              width: `${columnWidths.activityId}px`,
              minWidth: `${columnWidths.activityId}px`,
            }}
          >
            <span
              className={`
              font-mono px-1.5 py-0.5 rounded font-semibold truncate
              ${isTopLevel ? "text-xs" : "text-[11px]"}
              ${isSelected ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}
            `}
            >
              {node.code}
            </span>
          </div>

          {/* Name Column */}
          <div
            className="flex items-center flex-1 px-2 border-r border-slate-300 overflow-hidden"
            style={{
              minWidth: `${columnWidths.activityName}px`,
              paddingLeft: `${level * 16 + 8}px`,
            }}
          >
            {/* Toggle */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren || hasActivities) toggleExpand(node.id, e);
              }}
              className={`
                flex items-center justify-center w-5 h-5 rounded-sm mr-1 transition-colors flex-shrink-0
                ${hasChildren || hasActivities ? "hover:bg-slate-200 cursor-pointer" : "cursor-default"}
              `}
            >
              {hasChildren || hasActivities ? (
                isExpanded ? (
                  <ChevronDown
                    className={`${isTopLevel ? "h-4 w-4" : "h-3.5 w-3.5"} text-slate-500`}
                  />
                ) : (
                  <ChevronRight
                    className={`${isTopLevel ? "h-4 w-4" : "h-3.5 w-3.5"} text-slate-500`}
                  />
                )
              ) : (
                <span className="w-3.5" />
              )}
            </button>

            {/* Name Input/Text */}
            {isEditingWbs ? (
              <div
                className="flex items-center gap-1 flex-1 min-w-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Input
                  autoFocus
                  value={editingWbsName}
                  onChange={(e) => setEditingWbsName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSaveWbsEdit(node);
                    } else if (e.key === "Escape") {
                      handleCancelWbsEdit();
                    }
                  }}
                  className={`h-6 text-xs px-2 ${isTopLevel ? "font-bold" : "font-semibold"}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 flex-shrink-0"
                  onClick={() => handleSaveWbsEdit(node)}
                  disabled={savingWbs}
                >
                  {savingWbs ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Save className="h-3 w-3 text-green-600" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 flex-shrink-0"
                  onClick={handleCancelWbsEdit}
                >
                  <RotateCcw className="h-3 w-3 text-slate-400" />
                </Button>
              </div>
            ) : (
              <span
                className={`
                  truncate cursor-pointer
                  ${isTopLevel ? "text-sm font-bold" : "text-xs font-semibold"}
                  ${isSelected ? "text-amber-900" : "text-slate-800"}
                  ${canEditStructure() ? "hover:bg-amber-50 rounded px-1 -mx-1 transition-colors" : ""}
                `}
                onDoubleClick={(e) => handleStartWbsEdit(node, e)}
                title={
                  canEditStructure() ? "Double-click to edit name" : node.name
                }
              >
                {node.name}
              </span>
            )}

            {/* Count Badge */}
            {hasActivities && !isEditingWbs && (
              <span
                className={`
                text-[10px] px-1.5 py-0.5 rounded-full ml-2 tabular-nums flex-shrink-0
                ${isSelected ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"}
              `}
              >
                {nodeActivities.length}
              </span>
            )}

            {/* Comment Badge — opens the WBS comments dialog */}
            {!isEditingWbs && (
              <button
                className={cn(
                  "flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full ml-2 tabular-nums flex-shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400",
                  commentCounts[node.id]
                    ? "bg-sky-50 text-sky-600 hover:bg-sky-100"
                    : "bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setWbsCommentsTarget(node);
                }}
                title={commentBadgeLabel(commentCounts[node.id])}
                aria-label={commentBadgeLabel(commentCounts[node.id])}
                data-testid={`wbs-comments-badge-${node.id}`}
              >
                <MessageSquare className="h-3 w-3 flex-shrink-0" />
                {commentCounts[node.id] > 0 && <span>{commentCounts[node.id]}</span>}
              </button>
            )}

            {/* Hover Actions — WBS structure controls (PLAN level). */}
            {canEditStructure() && !isEditingWbs && (
              <div className="flex items-center gap-0.5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  className="p-1 rounded hover:bg-slate-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartWbsEdit(node, e);
                  }}
                  title="Edit WBS name"
                >
                  <Pencil className="h-3 w-3 text-slate-400 hover:text-blue-600" />
                </button>
                <button
                  className="p-1 rounded hover:bg-slate-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAddActivityDialog(node.id, node);
                  }}
                  title="Add Activity"
                >
                  <Plus className="h-3 w-3 text-slate-400 hover:text-green-600" />
                </button>
                <button
                  className="p-1 rounded hover:bg-slate-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAddWbsDialog(true, node.id);
                  }}
                  title="Add Child WBS"
                >
                  <FolderPlus className="h-3 w-3 text-slate-400 hover:text-amber-600" />
                </button>
                {onCopyWbs && (
                  <button
                    className="p-1 rounded hover:bg-slate-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopyWbs(node);
                    }}
                    title="Copy WBS (Ctrl+C)"
                  >
                    <Copy className="h-3 w-3 text-slate-400 hover:text-indigo-600" />
                  </button>
                )}
                {onPasteWbs && clipboardWbs && (
                  <button
                    className="p-1 rounded hover:bg-emerald-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPasteWbs(node);
                    }}
                    title={`Paste "${clipboardWbs.node.name}" here (Ctrl+V)`}
                  >
                    <ClipboardPaste className="h-3 w-3 text-slate-400 hover:text-emerald-600" />
                  </button>
                )}
                <button
                  className="p-1 rounded hover:bg-red-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingDeleteWbs(node);
                  }}
                  title="Delete WBS element"
                >
                  <Trash2 className="h-3 w-3 text-slate-400 hover:text-red-600" />
                </button>
              </div>
            )}
          </div>

          {/* Resources - Empty */}
          {isColumnVisible("resources") && (
            <div
              className="border-r border-slate-300 flex-shrink-0"
              style={{
                width: `${columnWidths.resources}px`,
                minWidth: `${columnWidths.resources}px`,
                maxWidth: `${columnWidths.resources}px`,
              }}
            />
          )}

          {/* Baseline Start - Empty (only in baseline mode) */}
          {isColumnVisible("baselineStart") && (
            <div
              className="border-r border-purple-200 flex-shrink-0 bg-purple-50/30"
              style={{
                width: `${columnWidths.baselineStart}px`,
                minWidth: `${columnWidths.baselineStart}px`,
                maxWidth: `${columnWidths.baselineStart}px`,
              }}
            />
          )}

          {/* Current Start (Start) - Empty */}
          {isColumnVisible("currentStart") && (
            <div
              className="border-r border-slate-300 flex-shrink-0"
              style={{
                width: `${columnWidths.currentStart}px`,
                minWidth: `${columnWidths.currentStart}px`,
                maxWidth: `${columnWidths.currentStart}px`,
              }}
            />
          )}

          {/* Duration - Empty (placed right after Start so the row aligns with the Start | Dur | Finish header order) */}
          {isColumnVisible("duration") && (
            <div
              className="border-r border-slate-300 flex-shrink-0"
              style={{
                width: `${columnWidths.duration}px`,
                minWidth: `${columnWidths.duration}px`,
                maxWidth: `${columnWidths.duration}px`,
              }}
            />
          )}

          {/* Baseline Finish - Empty (only in baseline mode) */}
          {isColumnVisible("baselineFinish") && (
            <div
              className="border-r border-purple-200 flex-shrink-0 bg-purple-50/30"
              style={{
                width: `${columnWidths.baselineFinish}px`,
                minWidth: `${columnWidths.baselineFinish}px`,
                maxWidth: `${columnWidths.baselineFinish}px`,
              }}
            />
          )}

          {/* Current Finish (Finish) - Empty */}
          {isColumnVisible("currentFinish") && (
            <div
              className="border-r border-slate-300 flex-shrink-0"
              style={{
                width: `${columnWidths.currentFinish}px`,
                minWidth: `${columnWidths.currentFinish}px`,
                maxWidth: `${columnWidths.currentFinish}px`,
              }}
            />
          )}

          {/* Actual Start - Empty */}
          {isColumnVisible("actualStart") && (
            <div className="border-r border-slate-300 flex-shrink-0 w-[90px] min-w-[90px] max-w-[90px]" />
          )}

          {/* Actual Finish - Empty */}
          {isColumnVisible("actualFinish") && (
            <div className="border-r border-slate-300 flex-shrink-0 w-[90px] min-w-[90px] max-w-[90px]" />
          )}

          {/* Expected Finish - Empty */}
          {isColumnVisible("expectedFinish") && (
            <div
              className="border-r border-amber-200 flex-shrink-0 bg-amber-50/30"
              style={{
                width: `${columnWidths.expectedFinish}px`,
                minWidth: `${columnWidths.expectedFinish}px`,
                maxWidth: `${columnWidths.expectedFinish}px`,
              }}
            />
          )}

          {/* Progress - Empty */}
          {isColumnVisible("progress") && (
            <div
              className="border-r border-slate-300 flex-shrink-0"
              style={{
                width: `${columnWidths.progress}px`,
                minWidth: `${columnWidths.progress}px`,
                maxWidth: `${columnWidths.progress}px`,
              }}
            />
          )}

          {/* Float - Empty */}
          {isColumnVisible("float") && (
            <div
              className="border-r border-slate-300 flex-shrink-0"
              style={{
                width: `${columnWidths.float}px`,
                minWidth: `${columnWidths.float}px`,
                maxWidth: `${columnWidths.float}px`,
              }}
            />
          )}

          {/* Status - Empty */}
          {isColumnVisible("status") && (
            <div
              className="flex-shrink-0"
              style={{
                width: `${columnWidths.status}px`,
                minWidth: `${columnWidths.status}px`,
                maxWidth: `${columnWidths.status}px`,
              }}
            />
          )}
        </div>

        {/* Children (WBS and Activities) */}
        {isExpanded && (
          <div className="relative">
            {/* Child WBS nodes */}
            {hasChildren &&
              node.children.map((child) => renderNode(child, level + 1))}

            {/* Activities under this WBS. The SortableContext that registers
                each row lives at the DndContext level so the KeyboardSensor
                can navigate between activities across WBS boundaries (the
                cross-WBS reorder case). */}
            {hasActivities &&
              nodeActivities.map((activity) =>
                renderActivityRow(activity, level + 1),
              )}
          </div>
        )}
      </div>
    );
  };

  // Render activity row (nested under WBS) with inline editing
  const renderActivityRow = (activity, level) => {
    const isSelected = selectedActivityId === activity.id;
    const isCritical = activity.total_float === 0 || activity.is_critical;
    const isEditing = editingActivityId === activity.id;
    const isShowingRelationships = showRelationshipsFor === activity.id;

    // Get relationships for this activity
    const predecessors = getPredecessors(activity.id);
    const successors = getSuccessors(activity.id);
    const hasRelationships = predecessors.length > 0 || successors.length > 0;

    // Render inline date picker for a field with actualization checkbox
    const renderInlineDatePicker = (field, label) => {
      const isEditingThis = isEditing && editingField === field;

      // Determine which value to display - actual takes precedence for display.
      // Precedence: actual → early (CPM forecast) → planned (user authored).
      // Plan zesty-coalescing-clarke.md (step 5): early before planned so the
      // CPM-derived dates for successors show through even if a stale
      // planned_start is still on the document. After the backend persistence
      // change (step 3), planned and early will be equal for predecessor-bound
      // activities anyway — this guards against legacy data and against the
      // narrow case where a user edited planned_start on a not-started successor.
      const isActualized =
        field === "start_date"
          ? !!activity.actual_start
          : !!activity.actual_finish;
      const displayValue =
        field === "start_date"
          ? selectActivityStart(activity)
          : selectActivityFinish(activity);

      if (isEditingThis) {
        return (
          <Popover open={true}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-6 w-20 text-[10px] px-1.5 justify-start font-normal"
                onClick={(e) => e.stopPropagation()}
              >
                <CalendarIcon className="mr-1 h-3 w-3" />
                {editValue ? format(parseISO(editValue), "MMM d") : "Pick"}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[280px] p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
              align="start"
              side="bottom"
              sideOffset={4}
              collisionPadding={16}
              onClick={(e) => e.stopPropagation()}
            >
              <CalendarComponent
                mode="single"
                selected={editValue ? parseISO(editValue) : undefined}
                onSelect={(date) => {
                  if (date) {
                    setEditValue(format(date, "yyyy-MM-dd"));
                  }
                }}
                disabled={(date) => {
                  // Deliberate exception to the canonical selectActivityStart/Finish
                  // priority: the picker should be constrained by what the user
                  // planned and what's actually happened, NOT by CPM's forecast
                  // (early_*). Otherwise the picker would refuse a date earlier
                  // than CPM's earliest feasible — which is the user's right to
                  // override. Display uses the canonical helper; constraint logic
                  // skips early_*.
                  if (field === "finish_date") {
                    // Finish must be >= start
                    const startStr =
                      activity.actual_start || activity.planned_start;
                    if (startStr) {
                      return (
                        date <
                        parseISO(
                          typeof startStr === "string"
                            ? startStr.split("T")[0]
                            : startStr,
                        )
                      );
                    }
                  }
                  if (field === "start_date") {
                    // Start must be <= finish
                    const finishStr =
                      activity.actual_finish || activity.planned_finish;
                    if (finishStr) {
                      return (
                        date >
                        parseISO(
                          typeof finishStr === "string"
                            ? finishStr.split("T")[0]
                            : finishStr,
                        )
                      );
                    }
                  }
                  return false;
                }}
                initialFocus
              />
              {/* Actualization Checkbox */}
              <div className="flex items-center gap-2 px-3 py-2 border-t bg-slate-50">
                <Checkbox
                  id={`mark-as-actual-${field}`}
                  checked={markAsActual}
                  onCheckedChange={(checked) => setMarkAsActual(checked)}
                />
                <Label
                  htmlFor={`mark-as-actual-${field}`}
                  className="text-[11px] font-medium text-slate-700 cursor-pointer select-none"
                >
                  Mark as Actual
                </Label>
              </div>
              {/* Optional Comment Input */}
              <div className="px-3 py-2 border-t">
                <Input
                  placeholder="Add comment (optional)"
                  value={editComment}
                  onChange={(e) => setEditComment(e.target.value)}
                  className="h-7 text-[10px]"
                  maxLength={500}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`comment-input-${field}`}
                />
              </div>
              {/* Cancel, Save controls */}
              <div className="flex items-center justify-end gap-1 p-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelActivityEdit();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-6 text-[10px]"
                  disabled={savingEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveActivityEdit(activity);
                  }}
                >
                  {savingEdit ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        );
      }

      // Display with "(a)" indicator if actualized
      return (
        <span
          className={cn(
            "text-[10px] tabular-nums text-center cursor-pointer hover:bg-slate-100 px-0.5 py-0.5 rounded transition-colors",
            canEditActivities() ? "hover:text-blue-600" : "",
            isActualized ? "font-medium" : "",
          )}
          onClick={(e) => handleStartActivityEdit(activity, field, e)}
          title={
            canEditActivities()
              ? `Click to edit ${label}${isActualized ? " (Actual)" : ""}`
              : label
          }
        >
          {formatDate(displayValue)}
          {isActualized ? " (a)" : ""}
        </span>
      );
    };

    // Render a dedicated, actual-only date cell. Edits hit /progress endpoint
    // (auto-syncs status, percent_complete, remaining_duration; backend
    // validates against data_date and triggers synchronous CPM).
    //
    // Uses the same shadcn Popover + Calendar shape as the row's
    // Current Start/Finish editor (renderCurrentStartFinishEditor) so the
    // user sees ONE calendar UI across all date fields. Differs from the
    // Current editor in two intentional ways:
    //   1. No "Mark as Actual" checkbox — an actual is already actual.
    //   2. Footer caption names the data_date constraint so the user
    //      understands why future dates are disabled (the picker mirrors
    //      the backend's HTTP-400 rule via the Calendar's disabled callback).
    //
    // editingField uses 'actual_start_only' / 'actual_finish_only' to keep
    // this state machine separate from the unified currentStart/currentFinish
    // editor, which uses 'start_date' / 'finish_date'.
    const renderActualOnlyDatePicker = (activity, field) => {
      const editKey =
        field === "actual_start" ? "actual_start_only" : "actual_finish_only";
      const isEditingThis = isEditing && editingField === editKey;
      const value = activity[field];
      const displayDate = value ? formatDate(value) : "—";
      const canEdit = canEditActuals();

      const cellTid =
        field === "actual_start"
          ? `actual-start-cell-${activity.id}`
          : `actual-finish-cell-${activity.id}`;

      if (isEditingThis) {
        return (
          <Popover open={true}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-6 w-20 text-[10px] px-1.5 justify-start font-normal"
                onClick={(e) => e.stopPropagation()}
                data-testid={cellTid}
              >
                <CalendarIcon className="mr-1 h-3 w-3" />
                {editValue ? format(parseISO(editValue), "MMM d") : "Pick"}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[280px] p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
              align="start"
              side="bottom"
              sideOffset={4}
              collisionPadding={16}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Shared body — single source of truth for actual-picker
                  popover content (calendar + footer caption + comment +
                  Save/Cancel). The same component is used in
                  ActivityBottomPanel so the row and panel chrome stay
                  identical (customer May 12 visual symmetry rule). */}
              <ActualDatePickerBody
                initialValue={value}
                field={field}
                activity={activity}
                projectDataDate={activeProject?.data_date}
                initialComment={editComment}
                saving={savingEdit}
                onSave={(formatted, comment) => {
                  setEditComment(comment);
                  // Pass the formatted value DIRECTLY to the saver so we
                  // don't race React's setState on editValue.
                  handleSaveActualEdit(activity, field, formatted);
                }}
                onCancel={handleCancelActivityEdit}
              />
            </PopoverContent>
          </Popover>
        );
      }

      // Display state: clickable when permitted, em-dash when null.
      // Locked when actual_finish is requested but actual_start missing
      // (constraint #9 invariant — surfaced as a non-clickable cell with
      // an explanatory tooltip).
      const startMissing = field === "actual_finish" && !activity.actual_start;
      const clickable = canEdit && !startMissing;
      return (
        <span
          className={cn(
            "text-[10px] tabular-nums text-center px-0.5 py-0.5 rounded transition-colors w-full",
            clickable
              ? "cursor-pointer hover:bg-slate-100 hover:text-blue-600"
              : "cursor-default text-slate-400",
            value ? "font-medium text-slate-700" : "",
          )}
          onClick={(e) => {
            if (!clickable) return;
            e.stopPropagation();
            setEditingActivityId(activity.id);
            setEditingField(editKey);
            setEditValue(value ? value.split("T")[0] : "");
            setEditComment("");
          }}
          title={
            clickable
              ? `Click to edit ${field === "actual_start" ? "Actual Start" : "Actual Finish"}`
              : startMissing
                ? "Set Actual Start first"
                : field === "actual_start"
                  ? "Actual Start"
                  : "Actual Finish"
          }
          data-testid={cellTid}
        >
          {displayDate}
        </span>
      );
    };

    // Render inline type selector
    const renderInlineTypeSelector = () => {
      const isEditingType = isEditing && editingField === "type";
      const currentType = activity.activity_type || "task";

      if (isEditingType) {
        return (
          <Select
            value={editValue}
            onValueChange={(val) => {
              setEditValue(val);
              // Auto-save on selection
              setTimeout(() => handleSaveActivityEdit(activity), 100);
            }}
          >
            <SelectTrigger
              className="h-5 w-20 text-[10px] px-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent onClick={(e) => e.stopPropagation()}>
              <SelectItem value="task">Task</SelectItem>
              <SelectItem value="milestone">Milestone</SelectItem>
              <SelectItem value="loe">LOE</SelectItem>
              <SelectItem value="wbs_summary">Summary</SelectItem>
            </SelectContent>
          </Select>
        );
      }

      return (
        <Badge
          variant="outline"
          className={`text-[9px] px-1.5 py-0 cursor-pointer hover:bg-slate-100 transition-colors ${
            canEditActivities() ? "hover:border-blue-300" : ""
          }`}
          onClick={(e) => handleStartActivityEdit(activity, "type", e)}
          title={canEditActivities() ? "Click to change type" : "Activity type"}
        >
          {currentType}
        </Badge>
      );
    };

    // Render expected finish date editor (only for in-progress activities, manual forecast)
    const renderExpectedFinishEditor = (activity) => {
      const isEditingExpFinish =
        isEditing && editingField === "expected_finish";
      // Cannot edit expected_finish if activity is actualized (has actual_finish)
      const isActualized = Boolean(activity.actual_finish);
      const canEdit = canEditActivities() && !isActualized;
      const displayValue = activity.expected_finish;

      // Show locked state if actualized
      if (isActualized) {
        return (
          <div
            className="flex items-center gap-1 text-[10px] text-slate-400 cursor-not-allowed"
            title="Locked: Activity has been actualized"
          >
            <Lock className="h-3 w-3" />
            {displayValue
              ? format(parseISO(displayValue.split("T")[0]), "MMM d")
              : "—"}
          </div>
        );
      }

      if (isEditingExpFinish) {
        return (
          <Popover open={true}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-6 w-20 text-[10px] px-1.5 justify-start font-normal border-amber-300"
                onClick={(e) => e.stopPropagation()}
              >
                <CalendarIcon className="mr-1 h-3 w-3 text-amber-600" />
                {editValue ? format(parseISO(editValue), "MMM d") : "Pick"}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[280px] p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
              align="start"
              side="bottom"
              sideOffset={4}
              collisionPadding={16}
              onClick={(e) => e.stopPropagation()}
            >
              <CalendarComponent
                mode="single"
                selected={editValue ? parseISO(editValue) : undefined}
                onSelect={(date) => {
                  if (date) {
                    setEditValue(format(date, "yyyy-MM-dd"));
                  }
                }}
                initialFocus
              />
              {/* Optional Comment Input */}
              <div className="px-3 py-2 border-t">
                <Input
                  placeholder="Add comment (optional)"
                  value={editComment}
                  onChange={(e) => setEditComment(e.target.value)}
                  className="h-7 text-[10px]"
                  maxLength={500}
                  onClick={(e) => e.stopPropagation()}
                  data-testid="comment-input-expected-finish"
                />
              </div>
              <div className="flex items-center justify-end gap-1 p-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelActivityEdit();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-6 text-[10px] bg-amber-600 hover:bg-amber-700"
                  disabled={savingEdit}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setSavingEdit(true);
                    try {
                      const updatePayload = {
                        expected_finish: editValue || null,
                      };
                      if (editComment.trim()) {
                        updatePayload.comment = editComment.trim();
                      }

                      await api.put(
                        `/activities/${activity.id}`,
                        updatePayload,
                      );

                      // Push to undo stack
                      if (setUndoStack) {
                        setUndoStack((prev) => {
                          const newEntry = {
                            id: Date.now(),
                            activityId: activity.id,
                            activityName: activity.name,
                            field: "expected_finish",
                            displayName: "Expected Finish",
                            oldValue:
                              activity.expected_finish?.split("T")[0] || null,
                            newValue: editValue || null,
                            timestamp: new Date().toISOString(),
                          };
                          const newStack = [newEntry, ...prev];
                          return newStack.slice(0, 20);
                        });
                      }

                      // Merge expected-finish update via parent; derivation propagates.
                      onActivitiesMerged?.([
                        { ...activity, expected_finish: editValue },
                      ]);
                      toast.success("Expected finish updated");
                      onActivityChange?.();
                    } catch (error) {
                      console.error("Failed to update expected finish:", error);
                      toast.error("Failed to update expected finish");
                    } finally {
                      setSavingEdit(false);
                      setEditingActivityId(null);
                      setEditingField(null);
                      setEditValue("");
                      setEditComment("");
                    }
                  }}
                >
                  {savingEdit ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        );
      }

      // Not editable: show dash
      if (!canEdit) {
        return (
          <span
            className="text-[10px] tabular-nums text-slate-300"
            title="Expected finish not editable for actualized activities"
          >
            —
          </span>
        );
      }

      // Display existing value or prompt to set
      return (
        <span
          className={cn(
            "text-[10px] tabular-nums text-center cursor-pointer px-0.5 py-0.5 rounded transition-colors",
            displayValue
              ? "text-amber-700 hover:bg-amber-100 font-medium"
              : "text-amber-400 hover:bg-amber-100 hover:text-amber-600",
          )}
          onClick={(e) => {
            e.stopPropagation();
            setEditingActivityId(activity.id);
            setEditingField("expected_finish");
            setEditValue(displayValue?.split("T")[0] || "");
            setOriginalEditValue(displayValue?.split("T")[0] || "");
          }}
          title={
            canEditActivities()
              ? "Click to set expected finish date (forecast)"
              : "Expected finish date"
          }
        >
          {displayValue ? formatDate(displayValue) : "Set"}
        </span>
      );
    };

    return (
      <SortableActivityRow
        key={activity.id}
        activity={activity}
        isCommittingThisRow={cascadePendingFor === activity.id}
        otherCommitInFlight={
          cascadePendingFor !== null && cascadePendingFor !== activity.id
        }
      >
        {({
          attributes: dragAttributes,
          listeners: dragListeners,
          isCommittingThisRow: rowCommitting,
          otherCommitInFlight: otherRowCommitting,
        }) => (
          <>
            {/* Activity Main Row - fixed height sync */}
            <div
              className={`
            group flex items-center border-l-2 transition-colors cursor-pointer px-2 border-b border-slate-300
            ${getStatusTextColorClass(activity)}
            ${
              isSelected
                ? "bg-blue-50 border-l-blue-500"
                : isCritical
                  ? "bg-amber-50/30 border-l-transparent hover:bg-amber-50/50 hover:border-l-amber-400"
                  : "border-l-transparent hover:bg-slate-50 hover:border-l-slate-300"
            }
          `}
              style={{
                height: `${ROW_HEIGHT}px`,
                minHeight: `${ROW_HEIGHT}px`,
              }}
              onClick={(e) => handleSelectActivity(activity, e)}
              data-testid={`activity-row-${activity.id}`}
              data-activity-id={activity.id}
            >
              {/* Drag handle — visible on hover or keyboard focus. ONLY this
              element carries the @dnd-kit listeners so inline inputs and
              checkboxes elsewhere on the row don't start a drag.
              Commit-barrier states:
                rowCommitting: THIS row's reorder is in flight → grip
                  swaps to a spinning Loader2, opacity stays 100 (so the
                  user sees the spinner without needing to hover).
                otherRowCommitting: another row is committing → grip
                  dimmed to 30%, cursor:wait, tabIndex=-1, listeners
                  detached (defense above useSortable.disabled). */}
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{ width: "20px", minWidth: "20px" }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* The drag handle is a STRUCTURAL edit affordance (reorder / move
                across WBS = EDIT_STRUCTURE, plan-level). Hide it for
                view/collaborate so they get no reorder affordance at all; the
                20px spacer stays to keep column alignment. handleReorder also
                guards (defense-in-depth). */}
                {canEditStructure() && (
                  <button
                    type="button"
                    {...(otherRowCommitting ? {} : dragAttributes)}
                    {...(otherRowCommitting ? {} : dragListeners)}
                    data-testid={`drag-handle-${activity.id}`}
                    aria-label={
                      rowCommitting
                        ? "Reorder in progress"
                        : otherRowCommitting
                          ? "Reorder blocked — another reorder in progress"
                          : "Drag to reorder"
                    }
                    tabIndex={otherRowCommitting ? -1 : 0}
                    disabled={otherRowCommitting}
                    className={`hover:bg-slate-200 rounded p-0.5 transition-opacity flex items-center justify-center ${
                      rowCommitting
                        ? "opacity-100 cursor-wait"
                        : otherRowCommitting
                          ? // Locked-but-not-busy: handle looks identical to idle
                            // (only visible on hover). The lock surfaces only when
                            // the user reaches for it, via `cursor-not-allowed`.
                            // Prior `opacity-30` made the whole list look broken;
                            // the user read it as "a lot of gap" / disabled UI.
                            "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 cursor-not-allowed"
                          : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus:opacity-100 cursor-grab active:cursor-grabbing"
                    }`}
                    style={{ minWidth: "18px", minHeight: "18px" }}
                  >
                    {rowCommitting ? (
                      <Loader2 className="h-3.5 w-3.5 text-slate-500 animate-spin" />
                    ) : (
                      <GripVertical className="h-3.5 w-3.5 text-slate-400" />
                    )}
                  </button>
                )}
              </div>

              {/* Bulk-select checkbox — leftmost cell. Stops propagation so the
              checkbox click doesn't also select the row for the detail panel. */}
              {canEditActivities() && (
                <div
                  className="flex items-center justify-center flex-shrink-0 border-r border-slate-300"
                  style={{ width: "24px", minWidth: "24px", maxWidth: "24px" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedActivityIds.has(activity.id)}
                    onCheckedChange={() => toggleActivitySelection(activity.id)}
                    aria-label={`Select ${activity.name} for bulk edit`}
                    data-testid={`bulk-select-${activity.id}`}
                    className="h-3.5 w-3.5"
                  />
                </div>
              )}

              {/* Links column */}
              <div
                className="flex items-center justify-center flex-shrink-0 border-r border-slate-300 overflow-hidden"
                style={{
                  width: `${columnWidths.links}px`,
                  minWidth: `${columnWidths.links}px`,
                  maxWidth: `${columnWidths.links}px`,
                }}
              >
                <button
                  className={`w-6 h-6 flex items-center justify-center rounded-sm transition-colors hover:bg-slate-200 cursor-pointer`}
                  onClick={(e) => toggleShowRelationships(activity.id, e)}
                  title={
                    hasRelationships
                      ? `${predecessors.length} pred, ${successors.length} succ - Click to expand`
                      : "Click to add predecessors/successors"
                  }
                >
                  {isShowingRelationships ? (
                    <ChevronDown className="h-3.5 w-3.5 text-indigo-500" />
                  ) : hasRelationships ? (
                    <Link2 className="h-3.5 w-3.5 text-indigo-500" />
                  ) : (
                    <Link2 className="h-3.5 w-3.5 text-slate-300 group-hover:text-indigo-400" />
                  )}
                </button>
              </div>

              {/* Activity ID column */}
              <div
                className="flex items-center flex-shrink-0 px-1 border-r border-slate-300 overflow-hidden"
                style={{
                  width: `${columnWidths.activityId}px`,
                  minWidth: `${columnWidths.activityId}px`,
                  maxWidth: `${columnWidths.activityId}px`,
                }}
              >
                <span
                  className={`
              text-[11px] font-mono px-2 py-1 rounded truncate w-full text-center
              ${isSelected ? "bg-blue-100" : isCritical ? "bg-amber-100" : "bg-slate-100"}
            `}
                >
                  {activity.activity_id}
                </span>
              </div>

              {/* Activity Name column */}
              <div
                className="flex items-center flex-1 px-2 gap-1 overflow-hidden border-r border-slate-300"
                style={{
                  minWidth: `${columnWidths.activityName}px`,
                  paddingLeft: `${level * 16 + 8}px`,
                }}
              >
                <span className="text-[12px] truncate font-normal leading-tight">
                  {activity.name}
                </span>

                {/* Relationship Count Indicator */}
                {hasRelationships && !isShowingRelationships && (
                  <span
                    className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 tabular-nums flex-shrink-0"
                    title={`${predecessors.length} predecessors, ${successors.length} successors`}
                  >
                    {predecessors.length + successors.length}
                  </span>
                )}

                {/* Comment Badge — opens the bottom panel on the Comments tab */}
                {onOpenComments && (
                  <button
                    className={cn(
                      "flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full tabular-nums flex-shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400",
                      commentCounts[activity.id]
                        ? "bg-sky-50 text-sky-600 hover:bg-sky-100"
                        : "bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenComments(activity);
                    }}
                    title={commentBadgeLabel(commentCounts[activity.id])}
                    aria-label={commentBadgeLabel(commentCounts[activity.id])}
                    data-testid={`activity-comments-badge-${activity.id}`}
                  >
                    <MessageSquare className="h-3 w-3 flex-shrink-0" />
                    {commentCounts[activity.id] > 0 && (
                      <span>{commentCounts[activity.id]}</span>
                    )}
                  </button>
                )}

                {/* Calendar Indicator - clickable to edit */}
                {canEditActivities() && (
                  <button
                    className={cn(
                      "flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full transition-colors flex-shrink-0",
                      editingCalendarFor === activity.id
                        ? "bg-purple-100 text-purple-700"
                        : activity.calendar_id
                          ? "bg-purple-50 text-purple-600 hover:bg-purple-100"
                          : "bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 opacity-0 group-hover:opacity-100",
                    )}
                    onClick={(e) => handleOpenCalendarEdit(activity, e)}
                    title={
                      activity.calendar_id
                        ? `Calendar: ${getCalendarName(activity.calendar_id)} - Click to change`
                        : "Click to set activity calendar"
                    }
                  >
                    <Calendar className="h-3 w-3 flex-shrink-0" />
                    {activity.calendar_id && (
                      <span className="truncate max-w-[60px]">
                        {getCalendarName(activity.calendar_id)}
                      </span>
                    )}
                  </button>
                )}
              </div>

              {/* Resources column */}
              {isColumnVisible("resources") && (
                <div
                  className="flex items-center justify-center flex-shrink-0 border-r border-slate-300 overflow-hidden"
                  style={{
                    width: `${columnWidths.resources}px`,
                    minWidth: `${columnWidths.resources}px`,
                    maxWidth: `${columnWidths.resources}px`,
                  }}
                >
                  {canEditActivities() && (
                    <button
                      className={cn(
                        "flex items-center justify-center gap-0.5 text-[10px] px-1.5 py-1 rounded-full transition-colors truncate max-w-full",
                        getActivityResourceAssignments(activity.id).length > 0
                          ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                          : "bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600",
                      )}
                      onClick={(e) => toggleShowResources(activity.id, e)}
                      title={
                        getActivityResourceAssignments(activity.id).length > 0
                          ? "View/manage assigned resources"
                          : "Assign resources to this activity"
                      }
                    >
                      {(() => {
                        const assignments = getActivityResourceAssignments(
                          activity.id,
                        );
                        if (assignments.length > 0) {
                          // Show icon based on first resource type, or mixed icon if multiple different types
                          const firstType = assignments[0].resource_type;
                          const IconComponent = getResourceTypeIcon(firstType);
                          return (
                            <IconComponent className="h-3 w-3 flex-shrink-0" />
                          );
                        }
                        return <Boxes className="h-3 w-3 flex-shrink-0" />;
                      })()}
                      {getActivityResourceAssignments(activity.id).length >
                        0 && (
                        <span className="text-[9px] truncate">
                          {getActivityResourceAssignments(activity.id).length}
                        </span>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Activity Details - visible on wider screens */}
              <div className="hidden lg:contents">
                {/* Baseline Start column (visible in baseline mode or when baseline columns selected) */}
                {isColumnVisible("baselineStart") && (
                  <div
                    className="flex items-center justify-center flex-shrink-0 border-r border-purple-200 overflow-hidden bg-purple-50/30"
                    style={{
                      width: `${columnWidths.baselineStart}px`,
                      minWidth: `${columnWidths.baselineStart}px`,
                      maxWidth: `${columnWidths.baselineStart}px`,
                    }}
                  >
                    {(() => {
                      // Use activity.id (UUID) for matching with baseline data
                      const baselineData = getBaselineDataForActivity(
                        activity.id,
                      );
                      // baseline_start is used in comparison mode, planned_start in baseline detail
                      const startDate =
                        baselineData?.baseline_start ||
                        baselineData?.planned_start;
                      return (
                        <span className="text-[10px] tabular-nums text-purple-600">
                          {startDate ? formatDate(startDate) : "—"}
                        </span>
                      );
                    })()}
                  </div>
                )}

                {/* Start column (unified: shows actual if exists, otherwise planned) */}
                {isColumnVisible("currentStart") && (
                  <div
                    className="flex items-center justify-center flex-shrink-0 border-r border-slate-300 overflow-hidden"
                    style={{
                      width: `${columnWidths.currentStart}px`,
                      minWidth: `${columnWidths.currentStart}px`,
                      maxWidth: `${columnWidths.currentStart}px`,
                    }}
                  >
                    {renderInlineDatePicker("start_date", "Start")}
                  </div>
                )}

                {/* Duration column - EDITABLE - triggers finish date recalculation.
                Placed right after Start so the row reads Start | Dur | Finish
                (the natural CPM triangle). */}
                {isColumnVisible("duration") && (
                  <div
                    className="flex items-center justify-center flex-shrink-0 border-r border-slate-300 overflow-hidden cursor-pointer hover:bg-slate-50"
                    style={{
                      width: `${columnWidths.duration}px`,
                      minWidth: `${columnWidths.duration}px`,
                      maxWidth: `${columnWidths.duration}px`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (canEditActivities()) {
                        // Use server-stored duration (server is the source of truth for calendar-based duration)
                        const currentDuration =
                          activity.original_duration ||
                          activity.remaining_duration ||
                          1;
                        setEditingActivityId(activity.id);
                        setEditingField("duration");
                        setEditValue(String(currentDuration));
                        setOriginalEditValue(String(currentDuration));
                      }
                    }}
                    title={
                      canEditActivities()
                        ? "Click to edit duration (recalculates finish date)"
                        : "Duration (days)"
                    }
                  >
                    {editingActivityId === activity.id &&
                    editingField === "duration" ? (
                      <div className="flex items-center gap-1 w-full px-1">
                        <input
                          type="number"
                          min="1"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleSaveActivityEdit(activity);
                            } else if (e.key === "Escape") {
                              handleCancelActivityEdit();
                            }
                          }}
                          className="w-8 h-5 text-[10px] text-center border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveActivityEdit(activity);
                          }}
                          className="text-emerald-600 hover:text-emerald-700"
                          disabled={savingEdit}
                        >
                          {savingEdit ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Save className="h-3 w-3" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelActivityEdit();
                          }}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <span
                        className="text-[11px] tabular-nums font-medium truncate"
                        title={
                          canEditActivities()
                            ? "Duration (days) - Click to edit"
                            : "Duration (days)"
                        }
                      >
                        {activity.original_duration ||
                          activity.remaining_duration ||
                          "—"}
                        d
                      </span>
                    )}
                  </div>
                )}

                {/* Baseline Finish column (visible in baseline mode or when baseline columns selected) */}
                {isColumnVisible("baselineFinish") && (
                  <div
                    className="flex items-center justify-center flex-shrink-0 border-r border-purple-200 overflow-hidden bg-purple-50/30"
                    style={{
                      width: `${columnWidths.baselineFinish}px`,
                      minWidth: `${columnWidths.baselineFinish}px`,
                      maxWidth: `${columnWidths.baselineFinish}px`,
                    }}
                  >
                    {(() => {
                      // Use activity.id (UUID) for matching with baseline data
                      const baselineData = getBaselineDataForActivity(
                        activity.id,
                      );
                      // baseline_finish is used in comparison mode, planned_finish in baseline detail
                      const finishDate =
                        baselineData?.baseline_finish ||
                        baselineData?.planned_finish;
                      return (
                        <span className="text-[10px] tabular-nums text-purple-600">
                          {finishDate ? formatDate(finishDate) : "—"}
                        </span>
                      );
                    })()}
                  </div>
                )}

                {/* Current Finish column (unified: shows actual if exists, otherwise planned) */}
                {isColumnVisible("currentFinish") && (
                  <div
                    className="flex items-center justify-center flex-shrink-0 border-r border-slate-300 overflow-hidden"
                    style={{
                      width: `${columnWidths.currentFinish}px`,
                      minWidth: `${columnWidths.currentFinish}px`,
                      maxWidth: `${columnWidths.currentFinish}px`,
                    }}
                  >
                    {renderInlineDatePicker("finish_date", "Finish")}
                  </div>
                )}

                {/* Actual Start column - inline editable, hits /progress endpoint.
                Width applied via Tailwind arbitrary classes instead of the
                style prop because the visual-edits babel plugin drops inline
                `style` from these specific cells, which broke the 90-px width
                and threw off header/body alignment. */}
                {isColumnVisible("actualStart") && (
                  <div className="flex items-center justify-center flex-shrink-0 border-r border-slate-300 overflow-hidden w-[90px] min-w-[90px] max-w-[90px]">
                    {renderActualOnlyDatePicker(activity, "actual_start")}
                  </div>
                )}

                {/* Actual Finish column - inline editable, hits /progress endpoint.
                See note on Actual Start above re: width via Tailwind classes. */}
                {isColumnVisible("actualFinish") && (
                  <div className="flex items-center justify-center flex-shrink-0 border-r border-slate-300 overflow-hidden w-[90px] min-w-[90px] max-w-[90px]">
                    {renderActualOnlyDatePicker(activity, "actual_finish")}
                  </div>
                )}

                {/* Expected Finish column (manual forecast date for in-progress activities) */}
                {isColumnVisible("expectedFinish") && (
                  <div
                    className="flex items-center justify-center flex-shrink-0 border-r border-amber-200 overflow-hidden bg-amber-50/30"
                    style={{
                      width: `${columnWidths.expectedFinish}px`,
                      minWidth: `${columnWidths.expectedFinish}px`,
                      maxWidth: `${columnWidths.expectedFinish}px`,
                    }}
                  >
                    {renderExpectedFinishEditor(activity)}
                  </div>
                )}

                {/* Progress Indicator */}
                {isColumnVisible("progress") && (
                  <div
                    className="flex items-center gap-1 justify-center px-1 py-1 rounded bg-slate-50 border-r border-slate-100 cursor-pointer hover:bg-slate-100 flex-shrink-0 overflow-hidden"
                    style={{
                      width: `${columnWidths.progress}px`,
                      minWidth: `${columnWidths.progress}px`,
                      maxWidth: `${columnWidths.progress}px`,
                    }}
                    onClick={(e) => handleOpenProgressUpdate(activity, e)}
                    title={
                      canEditActivities()
                        ? "Click to update progress"
                        : "Activity progress"
                    }
                  >
                    <div className="w-10 h-1.5 bg-slate-200 rounded-full overflow-hidden flex-shrink-0">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          activity.percent_complete >= 100
                            ? "bg-emerald-500"
                            : activity.percent_complete > 0
                              ? "bg-blue-500"
                              : "bg-slate-300",
                        )}
                        style={{
                          width: `${Math.min(100, activity.percent_complete || 0)}%`,
                        }}
                      />
                    </div>
                    <span className="text-[10px] font-medium tabular-nums w-8 text-right truncate">
                      {Math.round(activity.percent_complete || 0)}%
                    </span>
                  </div>
                )}

                {/* Float */}
                {isColumnVisible("float") && (
                  <div
                    className="flex items-center justify-center flex-shrink-0 border-r border-slate-300 overflow-hidden"
                    style={{
                      width: `${columnWidths.float}px`,
                      minWidth: `${columnWidths.float}px`,
                      maxWidth: `${columnWidths.float}px`,
                    }}
                  >
                    <span
                      className="text-[11px] tabular-nums font-medium truncate"
                      title="Total float"
                    >
                      {activity.total_float !== null
                        ? `${activity.total_float}d`
                        : "—"}
                    </span>
                  </div>
                )}

                {/* Status Badge */}
                {isColumnVisible("status") && (
                  <div
                    className="flex items-center justify-center flex-shrink-0 gap-1 overflow-hidden"
                    style={{
                      width: `${columnWidths.status}px`,
                      minWidth: `${columnWidths.status}px`,
                      maxWidth: `${columnWidths.status}px`,
                    }}
                  >
                    {getStatusBadge(activity.status, "small", activity)}
                    {isCritical && (
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                    )}
                  </div>
                )}
              </div>

              {/* Mobile: Compact status */}
              <div className="lg:hidden flex items-center gap-1 ml-auto">
                {activity.percent_complete > 0 && (
                  <span
                    className={cn(
                      "text-[10px] font-medium tabular-nums px-1.5 py-0.5 rounded",
                      activity.percent_complete >= 100
                        ? "bg-emerald-50 text-emerald-600"
                        : "bg-amber-50 text-amber-600",
                    )}
                  >
                    {Math.round(activity.percent_complete)}%
                  </span>
                )}
                {getStatusBadge(activity.status, "small", activity)}
              </div>
            </div>

            {/* Progress Update Panel */}
            {editingProgressFor === activity.id && (
              <div
                className="bg-blue-50/50 border-l-2 border-l-blue-400 py-3 px-4"
                style={{ paddingLeft: `${level * 24 + 36}px` }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide flex items-center gap-1.5">
                      <TrendingUp className="h-3.5 w-3.5" />
                      Progress Update
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => setEditingProgressFor(null)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Readonly summary: Status + Actual Start + Actual Finish.
                  Edit Actual dates via the Actual Start / Actual Finish columns above. */}
                  <div className="bg-white border border-blue-100 rounded px-3 py-2 text-xs">
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-500">Status:</span>
                        {getStatusBadge(activity.status, "small", activity)}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-500">Actual Start:</span>
                        <span
                          className={
                            activity.actual_start
                              ? "font-medium text-slate-700 tabular-nums"
                              : "text-slate-400"
                          }
                        >
                          {activity.actual_start
                            ? formatDate(activity.actual_start)
                            : "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-500">Actual Finish:</span>
                        <span
                          className={
                            activity.actual_finish
                              ? "font-medium text-slate-700 tabular-nums"
                              : "text-slate-400"
                          }
                        >
                          {activity.actual_finish
                            ? formatDate(activity.actual_finish)
                            : "—"}
                        </span>
                      </div>
                    </div>
                    <p className="text-[10px] text-blue-600 mt-1">
                      Edit Actual dates in the Actual Start / Actual Finish
                      columns above.
                    </p>
                  </div>

                  {/* Percent Complete Slider */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-blue-700">
                        Percent Complete
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={progressUpdate.percent_complete}
                          aria-invalid={!!progressUpdateError}
                          onChange={(e) => {
                            const raw = e.target.value;
                            // parseInt('') / parseInt('abc') → NaN. Treat as 0
                            // (empty input is a valid intermediate state).
                            const parsed = parseInt(raw, 10);
                            const val = Number.isNaN(parsed) ? 0 : parsed;
                            const outOfRange = val < 0 || val > 100;
                            // Surface a toast only on the transition into the
                            // error state — not every keystroke while invalid.
                            if (outOfRange && !progressUpdateError) {
                              toast.error("Percent must be between 0 and 100");
                            }
                            setProgressUpdateError(
                              outOfRange
                                ? "Percent must be between 0 and 100"
                                : null,
                            );
                            setProgressUpdate((prev) => ({
                              ...prev,
                              percent_complete: val,
                              // Only recompute remaining_duration when the value
                              // is valid; otherwise the derived field would jump
                              // around as the user types an invalid number.
                              remaining_duration: outOfRange
                                ? prev.remaining_duration
                                : calculateRemainingFromPercent(activity, val),
                            }));
                          }}
                          className={cn(
                            "h-6 w-14 text-xs text-center",
                            progressUpdateError &&
                              "border-red-400 focus-visible:ring-red-400",
                          )}
                        />
                        <span className="text-xs text-slate-500">%</span>
                      </div>
                    </div>
                    {progressUpdateError && (
                      <p
                        className="text-[10px] text-red-600 text-right"
                        role="alert"
                      >
                        {progressUpdateError}
                      </p>
                    )}
                    <Slider
                      value={[progressUpdate.percent_complete]}
                      onValueChange={([val]) => {
                        // Slider is bounded by max=100 step=5 — values are always
                        // valid, so a slider interaction also clears any prior
                        // typed-input error.
                        setProgressUpdateError(null);
                        setProgressUpdate((prev) => ({
                          ...prev,
                          percent_complete: val,
                          remaining_duration: calculateRemainingFromPercent(
                            activity,
                            val,
                          ),
                        }));
                      }}
                      max={100}
                      step={5}
                      className="w-full"
                    />
                  </div>

                  {/* Remaining Duration */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-blue-700">
                      Remaining Duration (days)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        value={progressUpdate.remaining_duration}
                        onChange={(e) => {
                          const val = Math.max(
                            0,
                            parseInt(e.target.value) || 0,
                          );
                          const originalDur = activity.original_duration || 1;
                          const percentFromRemaining = Math.round(
                            ((originalDur - val) / originalDur) * 100,
                          );
                          setProgressUpdate((prev) => ({
                            ...prev,
                            remaining_duration: val,
                            percent_complete: Math.min(
                              100,
                              Math.max(0, percentFromRemaining),
                            ),
                          }));
                        }}
                        className="h-8 w-20 text-sm"
                      />
                      <span className="text-xs text-slate-500">
                        of {activity.original_duration}d original
                      </span>
                    </div>
                  </div>

                  {/* Info about auto-status */}
                  <div className="text-[10px] text-blue-600 bg-blue-100 px-2 py-1.5 rounded">
                    <strong>Note:</strong> Status is auto-derived from{" "}
                    <strong>Actual Start</strong> and{" "}
                    <strong>Actual Finish</strong>. Edit those in the columns
                    above to change status.
                  </div>

                  {/* Optional Comment Input */}
                  <div className="space-y-1">
                    <Input
                      placeholder="Add comment (optional)"
                      value={editComment}
                      onChange={(e) => setEditComment(e.target.value)}
                      className="h-8 text-xs"
                      maxLength={500}
                      data-testid="progress-comment-input"
                    />
                  </div>

                  {/* Save/Cancel Buttons */}
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setEditingProgressFor(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                      onClick={() => handleSaveProgress(activity)}
                      disabled={savingProgress || !!progressUpdateError}
                    >
                      {savingProgress ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : null}
                      Save Progress
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Resources Panel */}
            {showResourcesFor === activity.id && (
              <div
                className="bg-emerald-50/50 border-l-2 border-l-emerald-400 py-3 px-4 overflow-hidden"
                style={{ paddingLeft: `${level * 24 + 36}px` }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col gap-3 max-w-full">
                  <div
                    className={`flex items-center gap-2 ${layoutMode === "side-by-side" ? "flex-wrap" : "justify-between"}`}
                  >
                    <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide flex items-center gap-1.5 flex-shrink-0">
                      <Users className="h-3.5 w-3.5" />
                      Resources (
                      {getActivityResourceAssignments(activity.id).length})
                    </span>
                    <div
                      className={`flex items-center gap-1 ${layoutMode === "side-by-side" ? "flex-wrap" : ""}`}
                    >
                      {canEditActivities() && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                            onClick={() => {
                              setShowAddResourceDialog(true);
                              setNewResourceAssignment({
                                resource_id: "",
                                units: 1,
                              });
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Assign
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => setShowCreateResourceDialog(true)}
                          >
                            New Resource
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => setShowResourcesFor(null)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {getActivityResourceAssignments(activity.id).length === 0 ? (
                    <div className="text-xs text-slate-400 italic bg-white rounded p-2 border border-dashed border-slate-300">
                      No resources assigned - click Assign to add resources
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {getActivityResourceAssignments(activity.id).map(
                        (assignment) => {
                          const IconComponent = getResourceTypeIcon(
                            assignment.resource_type,
                          );
                          return (
                            <div
                              key={assignment.id}
                              className={`flex items-center gap-2 text-xs bg-white rounded px-3 py-2 border border-emerald-100 shadow-sm ${layoutMode === "side-by-side" ? "flex-wrap" : ""}`}
                            >
                              <IconComponent className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                              <span className="font-medium text-slate-700 flex-1 min-w-0 truncate">
                                {assignment.resource_name}
                              </span>
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-600 border-emerald-200 flex-shrink-0"
                              >
                                {assignment.resource_type}
                              </Badge>
                              <span className="text-slate-500 tabular-nums flex-shrink-0">
                                {assignment.units} unit
                                {assignment.units !== 1 ? "s" : ""}
                              </span>
                              {canEditActivities() && (
                                <button
                                  className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                                  onClick={() =>
                                    handleRemoveResource(
                                      activity.id,
                                      assignment.id,
                                    )
                                  }
                                  title="Remove resource"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Calendar Edit Panel */}
            {editingCalendarFor === activity.id && (
              <div
                className="bg-purple-50/50 border-l-2 border-l-purple-400 py-3 px-4 overflow-hidden"
                style={{ paddingLeft: `${level * 24 + 36}px` }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col gap-3 max-w-full">
                  <div
                    className={`flex items-center gap-2 ${layoutMode === "side-by-side" ? "flex-wrap" : "justify-between"}`}
                  >
                    <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide flex items-center gap-1.5 flex-shrink-0">
                      <Calendar className="h-3.5 w-3.5" />
                      Activity Calendar
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => setEditingCalendarFor(null)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-purple-700">
                      Calendar Assignment
                    </Label>
                    <Select
                      value={selectedCalendarId || "project_default"}
                      onValueChange={(value) =>
                        setSelectedCalendarId(
                          value === "project_default" ? "" : value,
                        )
                      }
                    >
                      <SelectTrigger className="h-8 text-sm bg-white max-w-full">
                        <SelectValue placeholder="Select calendar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="project_default">
                          <span className="text-slate-400">
                            Project Default Calendar
                          </span>
                        </SelectItem>
                        {projectCalendars.map((cal) => (
                          <SelectItem key={cal.id} value={cal.id}>
                            <div className="flex items-center gap-2">
                              <span>{cal.name}</span>
                              {cal.is_default && (
                                <span className="text-[10px] text-blue-500 font-medium">
                                  (Default)
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-purple-600">
                      Override the project's default calendar for this
                      activity's scheduling calculations
                    </p>
                  </div>

                  {/* Optional Comment Input */}
                  <div className="space-y-1">
                    <Input
                      placeholder="Add comment (optional)"
                      value={editComment}
                      onChange={(e) => setEditComment(e.target.value)}
                      className="h-8 text-xs"
                      maxLength={500}
                      data-testid="calendar-comment-input"
                    />
                  </div>

                  <div
                    className={`flex items-center gap-2 ${layoutMode === "side-by-side" ? "flex-wrap justify-start" : "justify-end"}`}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setEditingCalendarFor(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-purple-600 hover:bg-purple-700"
                      onClick={() => handleSaveCalendar(activity)}
                      disabled={savingCalendar}
                    >
                      {savingCalendar ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : null}
                      Save Calendar
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Expanded Relationships Panel */}
            {isShowingRelationships && (
              <div
                className="bg-indigo-50/50 border-l-2 border-l-indigo-400 py-3 px-4 overflow-hidden"
                style={{ paddingLeft: `${level * 24 + 36}px` }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col gap-3 max-w-full">
                  {/* Predecessors Section */}
                  <div className="space-y-2">
                    <div
                      className={`flex items-center gap-2 ${layoutMode === "side-by-side" ? "flex-wrap" : "justify-between"}`}
                    >
                      <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide flex items-center gap-1.5 flex-shrink-0">
                        <ArrowRight className="h-3.5 w-3.5 rotate-180" />
                        Predecessors ({predecessors.length})
                      </span>
                      {canEditActivities() && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-100 border-indigo-200"
                          onClick={() =>
                            handleOpenAddRelation(activity.id, "predecessor")
                          }
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Predecessor
                        </Button>
                      )}
                    </div>
                    {predecessors.length === 0 ? (
                      <div className="text-xs text-slate-400 italic bg-white rounded p-2 border border-dashed border-slate-300">
                        No predecessors - click Add Predecessor to create one
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {predecessors.map((rel) => {
                          const predActivity = getActivityByCode(
                            rel.predecessor_activity_id,
                          );
                          return (
                            <div
                              key={rel.id}
                              className={`flex items-center gap-2 text-xs bg-white rounded px-3 py-2 border border-indigo-100 shadow-sm ${layoutMode === "side-by-side" ? "flex-wrap" : ""}`}
                            >
                              <span className="font-mono font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                {rel.predecessor_activity_id}
                              </span>
                              <span
                                className="text-slate-600 truncate flex-1 min-w-0"
                                title={predActivity?.name}
                              >
                                {predActivity?.name || "Unknown Activity"}
                              </span>
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 bg-indigo-50 text-indigo-600 border-indigo-200 flex-shrink-0"
                              >
                                {rel.relationship_type}
                              </Badge>
                              {rel.lag_days !== 0 && (
                                <span
                                  className={`text-[10px] font-medium flex-shrink-0 ${rel.lag_days > 0 ? "text-amber-600" : "text-green-600"}`}
                                >
                                  {rel.lag_days > 0 ? "+" : ""}
                                  {rel.lag_days}d
                                </span>
                              )}
                              {canEditActivities() && (
                                <button
                                  className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                                  onClick={() =>
                                    handleDeleteRelationship(rel.id)
                                  }
                                  title="Remove relationship"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Successors Section */}
                  <div className="space-y-2">
                    <div
                      className={`flex items-center gap-2 ${layoutMode === "side-by-side" ? "flex-wrap" : "justify-between"}`}
                    >
                      <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide flex items-center gap-1.5 flex-shrink-0">
                        <ArrowRight className="h-3.5 w-3.5" />
                        Successors ({successors.length})
                      </span>
                      {canEditActivities() && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-100 border-indigo-200"
                          onClick={() =>
                            handleOpenAddRelation(activity.id, "successor")
                          }
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Successor
                        </Button>
                      )}
                    </div>
                    {successors.length === 0 ? (
                      <div className="text-xs text-slate-400 italic bg-white rounded p-2 border border-dashed border-slate-300">
                        No successors - click Add Successor to create one
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {successors.map((rel) => {
                          const succActivity = getActivityByCode(
                            rel.successor_activity_id,
                          );
                          return (
                            <div
                              key={rel.id}
                              className={`flex items-center gap-2 text-xs bg-white rounded px-3 py-2 border border-indigo-100 shadow-sm ${layoutMode === "side-by-side" ? "flex-wrap" : ""}`}
                            >
                              <span className="font-mono font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                {rel.successor_activity_id}
                              </span>
                              <span
                                className="text-slate-600 truncate flex-1 min-w-0"
                                title={succActivity?.name}
                              >
                                {succActivity?.name || "Unknown Activity"}
                              </span>
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 bg-indigo-50 text-indigo-600 border-indigo-200 flex-shrink-0"
                              >
                                {rel.relationship_type}
                              </Badge>
                              {rel.lag_days !== 0 && (
                                <span
                                  className={`text-[10px] font-medium flex-shrink-0 ${rel.lag_days > 0 ? "text-amber-600" : "text-green-600"}`}
                                >
                                  {rel.lag_days > 0 ? "+" : ""}
                                  {rel.lag_days}d
                                </span>
                              )}
                              {canEditActivities() && (
                                <button
                                  className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                                  onClick={() =>
                                    handleDeleteRelationship(rel.id)
                                  }
                                  title="Remove relationship"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </SortableActivityRow>
    );
  };

  return (
    <div
      className="flex flex-col h-full bg-white"
      data-testid="unified-schedule-view"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-300 bg-slate-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Schedule Structure
            </h3>
            <div className="h-4 w-px bg-slate-300" />
            <span className="text-[10px] text-slate-400">
              {countNodes(wbsTree)} WBS • {totalActivities} Activities
            </span>
          </div>
          {(canEditActivities() || canEditStructure()) && (
            <div className="flex items-center gap-2">
              {/* Primary Add Activity Button — activity-level (EDIT_ACTIVITY) */}
              {canEditActivities() && (
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm"
                  onClick={() => {
                    // Open add activity dialog - if WBS exists, use first one
                    const firstWbs = wbsTree[0];
                    if (firstWbs) {
                      handleOpenAddActivityDialog(firstWbs.id, firstWbs);
                    } else {
                      toast.error("Create a WBS element first");
                    }
                  }}
                  data-testid="add-activity-button-primary"
                >
                  <Plus className="h-4 w-4" />
                  Add Activity
                </Button>
              )}

              {/* Add WBS Button — STRUCTURAL work (backend: POST /wbs =>
                  EDIT_STRUCTURE). Gated by its own action, not EDIT_ACTIVITY:
                  the two are co-leveled today, but gating-by-coincidence would
                  silently become a hole if the levels ever diverge. */}
              {canEditStructure() && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5 border-slate-300 hover:bg-slate-100"
                  onClick={() => handleOpenAddWbsDialog(false)}
                  data-testid="add-wbs-button"
                >
                  <FolderPlus className="h-4 w-4" />
                  Add WBS
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Bulk edit toolbar — visible only when ≥1 activity is checkbox-selected.
            Toolbar shows count, Clear, Bulk edit button, and Select all visible. */}
        {canEditActivities() && selectedActivityIds.size >= 1 && (
          <div
            className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded text-xs"
            data-testid="bulk-edit-toolbar"
          >
            <span
              className="font-medium text-blue-700"
              data-testid="bulk-selection-count"
            >
              {selectedActivityIds.size} selected
            </span>
            <button
              onClick={clearActivitySelection}
              className="text-blue-600 hover:text-blue-800 hover:underline"
              data-testid="bulk-clear-button"
            >
              Clear
            </button>
            <span className="text-slate-300">|</span>
            <button
              onClick={() => {
                // Select every activity currently visible after filters.
                // filteredActivitiesByWbs is the post-filter map; flatten it.
                const allVisible = Object.values(
                  filteredActivitiesByWbs,
                ).flat();
                setSelectedActivityIds(new Set(allVisible.map((a) => a.id)));
              }}
              className="text-blue-600 hover:text-blue-800 hover:underline"
              data-testid="bulk-select-all-visible-button"
            >
              Select all visible
            </button>
            <div className="ml-auto">
              <Button
                size="sm"
                className="h-6 px-2 text-[11px] gap-1 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={selectedActivityIds.size < 2}
                onClick={() => setBulkEditOpen(true)}
                data-testid="bulk-edit-open-button"
                title={
                  selectedActivityIds.size < 2
                    ? "Select at least 2 rows to bulk edit"
                    : "Open bulk edit panel"
                }
              >
                Bulk edit
              </Button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            placeholder="Search WBS or activities..."
            className="pl-8 pr-7 h-7 text-xs bg-white border-slate-300"
            value={activitySearch}
            onChange={(e) => onActivitySearchChange?.(e.target.value)}
            data-testid="schedule-search-input"
          />
          {activitySearch && (
            <button
              onClick={() => onActivitySearchChange?.("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-200"
            >
              <X className="h-3 w-3 text-slate-400" />
            </button>
          )}
        </div>

        {/* Expand/Collapse & view-action toolbar.
            Settings button lives here (not in the column-header strip) so the
            header row stays a pure column-label strip aligned with body rows. */}
        <div
          className="flex items-center justify-between mt-2"
          data-testid="schedule-toolbar"
        >
          <div className="flex items-center gap-2">
            <button
              className="text-[10px] text-blue-600 hover:text-blue-700 hover:underline"
              onClick={handleExpandAll}
            >
              Expand All
            </button>
            <span className="text-slate-300">|</span>
            <button
              className="text-[10px] text-blue-600 hover:text-blue-700 hover:underline"
              onClick={handleCollapseAll}
            >
              Collapse All
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button
              className={cn(
                "p-1 rounded hover:bg-slate-200 transition-colors",
                showColumnSettings
                  ? "bg-slate-200 text-slate-700"
                  : "text-slate-400",
              )}
              onClick={handleOpenColumnSettings}
              title="Column Settings"
              data-testid="column-settings-button"
            >
              <Settings className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tree Content */}
      <div
        className="flex-1 overflow-auto relative"
        ref={scrollContainerRef}
        onScroll={(e) => {
          // Sync vertical scroll with Gantt
          if (onVerticalScroll) {
            onVerticalScroll(e.target.scrollTop);
          }
        }}
      >
        <div className="min-w-full w-max">
          {/* Column Headers - Sticky inside scroll view for perfect alignment */}
          <div
            className="hidden lg:flex sticky top-0 z-20 items-center px-2 border-l-2 border-transparent border-b border-slate-300 bg-slate-100 text-[10px] font-semibold text-slate-600 uppercase tracking-wide select-none min-w-full w-max"
            style={{
              height: `${HEADER_HEIGHT}px`,
              minHeight: `${HEADER_HEIGHT}px`,
            }}
            data-testid="column-header-row"
          >
            {/* Bulk-select - Empty placeholder. Position 1 so the column header
                lines up pixel-for-pixel with the activity-row checkbox cell.
                Gated on edit perms — same gate as the body checkbox. */}
            {canEditActivities() && (
              <div
                className="flex items-center justify-center flex-shrink-0 border-r border-slate-300"
                style={{ width: "24px", minWidth: "24px", maxWidth: "24px" }}
                data-testid="bulk-select-header-placeholder"
              />
            )}

            {/* Links column - Always visible */}
            <div
              className="flex items-center justify-center flex-shrink-0 relative group border-r border-slate-300 overflow-hidden"
              style={{
                width: `${columnWidths.links}px`,
                minWidth: `${columnWidths.links}px`,
                maxWidth: `${columnWidths.links}px`,
              }}
            >
              <Link2 className="h-3.5 w-3.5 text-slate-400" title="Links" />
              <div
                className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-slate-300"
                onMouseDown={(e) => handleResizeStart(e, "links")}
              />
            </div>

            {/* Activity ID column - Always visible */}
            <div
              className="flex items-center justify-center flex-shrink-0 px-1 relative group border-r border-slate-300 overflow-hidden"
              style={{
                width: `${columnWidths.activityId}px`,
                minWidth: `${columnWidths.activityId}px`,
                maxWidth: `${columnWidths.activityId}px`,
              }}
            >
              <span className="truncate">ID</span>
              <div
                className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-slate-300"
                onMouseDown={(e) => handleResizeStart(e, "activityId")}
              />
            </div>

            {/* Activity Name column - Always visible */}
            <div
              className="flex items-center px-2 relative group flex-1 border-r border-slate-300 overflow-hidden"
              style={{ minWidth: `${columnWidths.activityName}px` }}
            >
              <span className="truncate">Activity Name</span>
              <div
                className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-slate-300"
                onMouseDown={(e) => handleResizeStart(e, "activityName")}
              />
            </div>

            {/* Resources column */}
            {isColumnVisible("resources") && (
              <div
                className="flex items-center justify-center flex-shrink-0 relative group border-r border-slate-300 overflow-hidden"
                style={{
                  width: `${columnWidths.resources}px`,
                  minWidth: `${columnWidths.resources}px`,
                  maxWidth: `${columnWidths.resources}px`,
                }}
              >
                <Boxes
                  className="h-3.5 w-3.5 text-slate-400"
                  title="Resources"
                />
                <div
                  className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-slate-300"
                  onMouseDown={(e) => handleResizeStart(e, "resources")}
                />
              </div>
            )}

            {/* Baseline Start column (only visible in baseline mode) */}
            {isColumnVisible("baselineStart") && (
              <div
                className="flex items-center justify-center flex-shrink-0 px-1 relative group border-r border-purple-200 bg-purple-50/50 overflow-hidden"
                style={{
                  width: `${columnWidths.baselineStart}px`,
                  minWidth: `${columnWidths.baselineStart}px`,
                  maxWidth: `${columnWidths.baselineStart}px`,
                }}
              >
                <span className="text-purple-600 truncate">BL Start</span>
                <div
                  className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-purple-400 group-hover:bg-purple-300"
                  onMouseDown={(e) => handleResizeStart(e, "baselineStart")}
                />
              </div>
            )}

            {/* Start column (unified: plan/actual with (a) indicator) */}
            {isColumnVisible("currentStart") && (
              <div
                className="flex items-center justify-center flex-shrink-0 px-1 relative group border-r border-slate-300 overflow-hidden"
                style={{
                  width: `${columnWidths.currentStart}px`,
                  minWidth: `${columnWidths.currentStart}px`,
                  maxWidth: `${columnWidths.currentStart}px`,
                }}
              >
                <span className="text-slate-600 truncate">Start</span>
                <div
                  className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-slate-300"
                  onMouseDown={(e) => handleResizeStart(e, "currentStart")}
                />
              </div>
            )}

            {/* Duration column header — placed right after Start so the row reads Start | Dur | Finish */}
            {isColumnVisible("duration") && (
              <div
                className="flex items-center justify-center flex-shrink-0 px-1 relative group border-r border-slate-300 overflow-hidden"
                style={{
                  width: `${columnWidths.duration}px`,
                  minWidth: `${columnWidths.duration}px`,
                  maxWidth: `${columnWidths.duration}px`,
                }}
              >
                <span>Dur</span>
                <div
                  className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-slate-300"
                  onMouseDown={(e) => handleResizeStart(e, "duration")}
                />
              </div>
            )}

            {/* Baseline Finish column (only visible in baseline mode) */}
            {isColumnVisible("baselineFinish") && (
              <div
                className="flex items-center justify-center flex-shrink-0 px-1 relative group border-r border-purple-200 bg-purple-50/50 overflow-hidden"
                style={{
                  width: `${columnWidths.baselineFinish}px`,
                  minWidth: `${columnWidths.baselineFinish}px`,
                  maxWidth: `${columnWidths.baselineFinish}px`,
                }}
              >
                <span className="text-purple-600 truncate">BL Finish</span>
                <div
                  className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-purple-400 group-hover:bg-purple-300"
                  onMouseDown={(e) => handleResizeStart(e, "baselineFinish")}
                />
              </div>
            )}

            {/* Current Finish column (unified: plan/actual with (a) indicator) */}
            {isColumnVisible("currentFinish") && (
              <div
                className="flex items-center justify-center flex-shrink-0 px-1 relative group border-r border-slate-300 overflow-hidden"
                style={{
                  width: `${columnWidths.currentFinish}px`,
                  minWidth: `${columnWidths.currentFinish}px`,
                  maxWidth: `${columnWidths.currentFinish}px`,
                }}
              >
                <span className="text-slate-600 truncate">Finish</span>
                <div
                  className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-slate-300"
                  onMouseDown={(e) => handleResizeStart(e, "currentFinish")}
                />
              </div>
            )}

            {/* Actual Start column.
                Resize handle intentionally omitted — width is hardcoded via Tailwind
                arbitrary classes (workaround for visual-edits plugin stripping
                inline `style` on these specific cells). A handle here would be
                a dead UI affordance. */}
            {isColumnVisible("actualStart") && (
              <div className="flex items-center justify-center flex-shrink-0 px-1 relative border-r border-slate-300 overflow-hidden w-[90px] min-w-[90px] max-w-[90px]">
                <span className="text-slate-600 truncate">Actual Start</span>
              </div>
            )}

            {/* Actual Finish column — see resize-handle note above */}
            {isColumnVisible("actualFinish") && (
              <div className="flex items-center justify-center flex-shrink-0 px-1 relative border-r border-slate-300 overflow-hidden w-[90px] min-w-[90px] max-w-[90px]">
                <span className="text-slate-600 truncate">Actual Finish</span>
              </div>
            )}

            {/* Expected Finish column (manual forecast date for in-progress activities) */}
            {isColumnVisible("expectedFinish") && (
              <div
                className="flex items-center justify-center flex-shrink-0 px-1 relative group border-r border-amber-200 bg-amber-50/30 overflow-hidden"
                style={{
                  width: `${columnWidths.expectedFinish}px`,
                  minWidth: `${columnWidths.expectedFinish}px`,
                  maxWidth: `${columnWidths.expectedFinish}px`,
                }}
              >
                <span className="text-amber-700 truncate">Exp Finish</span>
                <div
                  className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-amber-400 group-hover:bg-amber-300"
                  onMouseDown={(e) => handleResizeStart(e, "expectedFinish")}
                />
              </div>
            )}

            {/* Progress column */}
            {isColumnVisible("progress") && (
              <div
                className="flex items-center justify-center flex-shrink-0 px-1 relative group border-r border-slate-200 overflow-hidden"
                style={{
                  width: `${columnWidths.progress}px`,
                  minWidth: `${columnWidths.progress}px`,
                  maxWidth: `${columnWidths.progress}px`,
                }}
              >
                <span>Progress</span>
                <div
                  className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-slate-300"
                  onMouseDown={(e) => handleResizeStart(e, "progress")}
                />
              </div>
            )}

            {/* Float column */}
            {isColumnVisible("float") && (
              <div
                className="flex items-center justify-center flex-shrink-0 px-1 relative group border-r border-slate-200 overflow-hidden"
                style={{
                  width: `${columnWidths.float}px`,
                  minWidth: `${columnWidths.float}px`,
                  maxWidth: `${columnWidths.float}px`,
                }}
              >
                <span>Float</span>
                <div
                  className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 group-hover:bg-slate-300"
                  onMouseDown={(e) => handleResizeStart(e, "float")}
                />
              </div>
            )}

            {/* Status column */}
            {isColumnVisible("status") && (
              <div
                className="flex items-center justify-center flex-shrink-0 px-1 overflow-hidden"
                style={{
                  width: `${columnWidths.status}px`,
                  minWidth: `${columnWidths.status}px`,
                  maxWidth: `${columnWidths.status}px`,
                }}
              >
                <span>Status</span>
              </div>
            )}
          </div>

          {/* Column Visibility Settings Dialog */}
          <Dialog
            open={showColumnSettings}
            onOpenChange={(open) => !open && handleCancelColumnSettings()}
          >
            <DialogContent
              className="sm:max-w-[400px]"
              data-testid="column-settings-dialog"
            >
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-slate-500" />
                  Column Visibility Settings
                </DialogTitle>
                <DialogDescription>
                  Select which columns to display in the activity table.
                </DialogDescription>
              </DialogHeader>

              <div className="py-4">
                <div className="space-y-3">
                  {getToggleableColumns().map((columnKey) => {
                    const isBaseline =
                      columnKey === "baselineStart" ||
                      columnKey === "baselineFinish";
                    const isBaselineAvailable =
                      baselineComparisonData || baselineColumnsData;
                    const isChecked = !tempHiddenColumns[columnKey];

                    // For baseline columns, they follow baseline mode - show as disabled
                    if (isBaseline) {
                      return (
                        <div
                          key={columnKey}
                          className="flex items-center gap-3 p-2 rounded bg-slate-50"
                        >
                          <Checkbox
                            id={`col-${columnKey}`}
                            checked={isBaselineAvailable}
                            disabled
                            className="opacity-50"
                          />
                          <Label
                            htmlFor={`col-${columnKey}`}
                            className="text-sm text-slate-400 cursor-not-allowed flex-1"
                          >
                            {COLUMN_DISPLAY_NAMES[columnKey]}
                          </Label>
                          <span className="text-[10px] text-slate-400">
                            (auto)
                          </span>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={columnKey}
                        className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 transition-colors"
                      >
                        <Checkbox
                          id={`col-${columnKey}`}
                          checked={isChecked}
                          onCheckedChange={() => handleToggleColumn(columnKey)}
                          data-testid={`column-checkbox-${columnKey}`}
                        />
                        <Label
                          htmlFor={`col-${columnKey}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {COLUMN_DISPLAY_NAMES[columnKey]}
                        </Label>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-200">
                  <p className="text-[11px] text-slate-400">
                    ID, Link & Activity Name columns are always visible.
                  </p>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={handleCancelColumnSettings}
                  data-testid="column-settings-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApplyColumnSettings}
                  data-testid="column-settings-apply"
                >
                  Apply
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : filteredTree.length === 0 ? (
              <div className="text-center py-12 px-4">
                <img
                  src={wbsLogo}
                  alt=""
                  className="h-12 w-12 mx-auto mb-3 opacity-30 object-contain"
                />
                <p className="text-sm font-medium text-slate-600 mb-1">
                  {activitySearch
                    ? "No matching items"
                    : "No Schedule Structure"}
                </p>
                <p className="text-xs text-slate-400 mb-4">
                  {activitySearch
                    ? "Try a different search term"
                    : "Create WBS elements and activities to build your schedule"}
                </p>
                {canEditStructure() && !activitySearch && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleOpenAddWbsDialog(false)}
                  >
                    <FolderPlus className="h-3 w-3 mr-1" />
                    Add WBS Element
                  </Button>
                )}
              </div>
            ) : (
              <div
                className="min-w-max"
                style={cascadePendingFor ? { cursor: "wait" } : undefined}
              >
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={reorderCollisionDetection}
                  measuring={dndMeasuring}
                  onDragEnd={handleReorder}
                >
                  <SortableContext
                    items={sortableItemIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredTree.map((node) => renderNode(node))}
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <img src={wbsLogo} alt="" className="h-3 w-3 object-contain" />
            <span className="text-[10px] text-slate-500">WBS</span>
          </div>
          <div className="flex items-center gap-1.5">
            <img src={activityLogo} alt="" className="h-3 w-3 object-contain" />
            <span className="text-[10px] text-slate-500">Activity</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-[10px] text-slate-500">Critical</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Link2 className="w-3 h-3 text-indigo-400" />
            <span className="text-[10px] text-slate-500">Has Links</span>
          </div>
          {/* Baseline legend - shown when baseline is selected */}
          {baselineColumnsData && (
            <div className="flex items-center gap-1.5 pl-2 border-l border-slate-300">
              <div className="w-2 h-2 rounded-full bg-purple-500" />
              <span className="text-[10px] text-slate-500">Baseline</span>
            </div>
          )}
        </div>
        {selectedWbsId && (
          <button
            className="text-[10px] text-blue-600 hover:underline"
            onClick={() => {
              setSelectedWbsId(null);
              setSelectedActivityId(null);
            }}
          >
            Clear Selection
          </button>
        )}
      </div>

      {/* Add WBS Dialog */}
      <Dialog open={showAddWbsDialog} onOpenChange={setShowAddWbsDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <img src={wbsLogo} alt="" className="h-4 w-4 object-contain" />
              {addWbsAsChild ? "Add Child WBS" : "Add WBS Element"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {addWbsAsChild
                ? "Create a child element under the selected WBS"
                : "Create a new root WBS element"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateWbs}>
            <div className="space-y-3 py-3">
              <div className="space-y-1.5">
                <Label htmlFor="wbs-code" className="text-xs font-medium">
                  WBS Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wbs-code"
                  placeholder="e.g., 1.0 or 1.1.1"
                  value={newWbs.code}
                  onChange={(e) =>
                    setNewWbs({ ...newWbs, code: e.target.value })
                  }
                  className="h-8 text-sm font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wbs-name" className="text-xs font-medium">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wbs-name"
                  placeholder="WBS element name"
                  value={newWbs.name}
                  onChange={(e) =>
                    setNewWbs({ ...newWbs, name: e.target.value })
                  }
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label
                  htmlFor="wbs-description"
                  className="text-xs font-medium"
                >
                  Description
                </Label>
                <Input
                  id="wbs-description"
                  placeholder="Optional description"
                  value={newWbs.description}
                  onChange={(e) =>
                    setNewWbs({ ...newWbs, description: e.target.value })
                  }
                  className="h-8 text-sm"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAddWbsDialog(false)}
                disabled={isCreatingWbs}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={isCreatingWbs}
              >
                {isCreatingWbs ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create WBS"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete WBS confirmation
          onOpenChange is guarded against close attempts while the DELETE
          request is in flight (isDeletingWbs=true), so the user cannot
          dismiss the dialog mid-call via backdrop click or Escape. Same
          contract as the activity delete dialog in ActivityBottomPanel. */}
      <AlertDialog
        open={!!pendingDeleteWbs}
        onOpenChange={(open) => {
          if (isDeletingWbs) return;
          if (!open) setPendingDeleteWbs(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete WBS element?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{pendingDeleteWbs?.name}</strong> and everything below it
              will be permanently deleted
              {" — "}
              {countDescendantWbs(pendingDeleteWbs)} sub-element
              {countDescendantWbs(pendingDeleteWbs) === 1 ? "" : "s"}
              {" and "}
              {sumActivities(pendingDeleteWbs)} activit
              {sumActivities(pendingDeleteWbs) === 1 ? "y" : "ies"}. All
              dependency arrows touching these activities will be removed. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingWbs}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWbs}
              disabled={isDeletingWbs}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeletingWbs ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cross-WBS reorder confirmation. The drag-end staged the move
          in pendingCrossWbs (no POST yet); this dialog gates the actual
          commit. Cancel = discard, no POST. Move = commit with
          allow_cross_wbs=true. Replaces the prior silent same-WBS-only
          early-return that made cross-WBS drags look broken. */}
      <AlertDialog
        open={!!pendingCrossWbs}
        onOpenChange={(open) => {
          if (!open) cancelCrossWbsMove();
        }}
      >
        <AlertDialogContent data-testid="cross-wbs-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Move activity to another WBS?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingCrossWbs &&
                (() => {
                  const src = findWbsNode(pendingCrossWbs.sourceWbsId);
                  const dst = findWbsNode(pendingCrossWbs.targetWbsId);
                  return (
                    <>
                      Move <strong>{pendingCrossWbs.moved?.name}</strong> from{" "}
                      <strong>{src?.name || src?.code || "current WBS"}</strong>{" "}
                      to <strong>{dst?.name || dst?.code || "new WBS"}</strong>?{" "}
                      This will reparent the activity and may shift its dates.
                    </>
                  );
                })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelCrossWbsMove}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmCrossWbsMove}>
              Move
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Activity Dialog - Condensed & Responsive */}
      <Dialog
        open={showAddActivityDialog}
        onOpenChange={setShowAddActivityDialog}
      >
        <DialogContent className="max-w-[95vw] sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              Add Activity
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Create a new activity. Activity ID will be auto-generated.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateActivity}>
            <div className="space-y-4 py-3">
              {/* Activity Name - Full Width */}
              <div className="space-y-1.5">
                <Label htmlFor="activity-name" className="text-xs font-medium">
                  Activity Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="activity-name"
                  placeholder="Enter activity name"
                  value={newActivity.name}
                  onChange={(e) =>
                    setNewActivity({ ...newActivity, name: e.target.value })
                  }
                  className="h-9 text-sm"
                  autoFocus
                />
              </div>

              {/* Dates Section - Responsive Grid */}
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Dates
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Planned Start */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full h-9 text-sm justify-start text-left font-normal",
                            !newActivity.planned_start &&
                              "text-muted-foreground",
                          )}
                          data-testid="planned-start-picker"
                        >
                          <CalendarIcon className="mr-2 h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">
                            {newActivity.planned_start
                              ? format(
                                  parseISO(newActivity.planned_start),
                                  "MMM d, yyyy",
                                )
                              : "Pick date"}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={
                            newActivity.planned_start
                              ? parseISO(newActivity.planned_start)
                              : undefined
                          }
                          onSelect={(date) => {
                            if (date) {
                              const newStart = format(date, "yyyy-MM-dd");
                              const updatedActivity = {
                                ...newActivity,
                                planned_start: newStart,
                              };

                              if (newActivity.planned_finish) {
                                if (newActivity.planned_finish < newStart) {
                                  // Finish is before new start — recalculate finish from duration
                                  const dur =
                                    parseInt(newActivity.original_duration) ||
                                    1;
                                  updatedActivity.planned_finish = format(
                                    addDays(date, dur - 1),
                                    "yyyy-MM-dd",
                                  );
                                  updatedActivity.original_duration = dur;
                                } else {
                                  // Finish exists and is valid — recalculate duration
                                  const days =
                                    differenceInDays(
                                      parseISO(newActivity.planned_finish),
                                      date,
                                    ) + 1;
                                  updatedActivity.original_duration = Math.max(
                                    1,
                                    days,
                                  );
                                }
                              } else {
                                // No finish yet — calculate finish from duration
                                const dur =
                                  parseInt(newActivity.original_duration) || 1;
                                updatedActivity.planned_finish = format(
                                  addDays(date, dur - 1),
                                  "yyyy-MM-dd",
                                );
                              }
                              setNewActivity(updatedActivity);
                            } else {
                              setNewActivity({
                                ...newActivity,
                                planned_start: "",
                              });
                            }
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Planned Finish */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Finish Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full h-9 text-sm justify-start text-left font-normal",
                            !newActivity.planned_finish &&
                              "text-muted-foreground",
                          )}
                          data-testid="planned-finish-picker"
                        >
                          <CalendarIcon className="mr-2 h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">
                            {newActivity.planned_finish
                              ? format(
                                  parseISO(newActivity.planned_finish),
                                  "MMM d, yyyy",
                                )
                              : "Pick date"}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={
                            newActivity.planned_finish
                              ? parseISO(newActivity.planned_finish)
                              : undefined
                          }
                          onSelect={(date) => {
                            if (date) {
                              const newFinish = format(date, "yyyy-MM-dd");
                              const updatedActivity = {
                                ...newActivity,
                                planned_finish: newFinish,
                              };

                              if (newActivity.planned_start) {
                                // Start exists — recalculate duration
                                const days =
                                  differenceInDays(
                                    date,
                                    parseISO(newActivity.planned_start),
                                  ) + 1;
                                updatedActivity.original_duration = Math.max(
                                  1,
                                  days,
                                );
                              }
                              setNewActivity(updatedActivity);
                            } else {
                              setNewActivity({
                                ...newActivity,
                                planned_finish: "",
                              });
                            }
                          }}
                          initialFocus
                          disabled={(date) => {
                            if (newActivity.planned_start) {
                              const startDate = parseISO(
                                newActivity.planned_start,
                              );
                              return date < startDate;
                            }
                            return false;
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              {/* Duration & Status Section - Responsive Grid */}
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Duration & Status
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Duration */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      Duration (days)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={newActivity.original_duration}
                      onChange={(e) => {
                        const dur = parseInt(e.target.value) || 1;
                        const updatedActivity = {
                          ...newActivity,
                          original_duration: e.target.value,
                        };

                        if (newActivity.planned_start && dur >= 1) {
                          // Start exists — recalculate finish
                          updatedActivity.planned_finish = format(
                            addDays(
                              parseISO(newActivity.planned_start),
                              dur - 1,
                            ),
                            "yyyy-MM-dd",
                          );
                        } else if (
                          newActivity.planned_finish &&
                          !newActivity.planned_start &&
                          dur >= 1
                        ) {
                          // Only finish exists — recalculate start
                          updatedActivity.planned_start = format(
                            addDays(
                              parseISO(newActivity.planned_finish),
                              -(dur - 1),
                            ),
                            "yyyy-MM-dd",
                          );
                        }
                        setNewActivity(updatedActivity);
                      }}
                      className="h-9 text-sm"
                    />
                  </div>

                  {/* Status */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Status</Label>
                    <Select
                      value={newActivity.status}
                      onValueChange={(value) =>
                        setNewActivity({ ...newActivity, status: value })
                      }
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">Not Started</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Calendar Selection - Full Width */}
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Assignment
                </h4>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Calendar</Label>
                  <Select
                    value={newActivity.calendar_id || "project_default"}
                    onValueChange={(value) =>
                      setNewActivity({
                        ...newActivity,
                        calendar_id: value === "project_default" ? "" : value,
                      })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Use project default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project_default">
                        <span className="text-slate-400">
                          Project Default Calendar
                        </span>
                      </SelectItem>
                      {projectCalendars.map((cal) => (
                        <SelectItem key={cal.id} value={cal.id}>
                          <div className="flex items-center gap-2">
                            <span>{cal.name}</span>
                            {cal.is_default && (
                              <span className="text-[10px] text-blue-500 font-medium">
                                (Default)
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAddActivityDialog(false)}
                disabled={isCreatingActivity}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto"
                disabled={isCreatingActivity}
              >
                {isCreatingActivity ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Activity"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Relationship Dialog */}
      <Dialog
        open={showAddRelationDialog}
        onOpenChange={setShowAddRelationDialog}
      >
        <DialogContent
          className="sm:max-w-[620px]"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <Link2 className="h-4 w-4 text-indigo-500" />
              Add{" "}
              {addRelationType === "predecessor" ? "Predecessor" : "Successor"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {addRelationType === "predecessor"
                ? "Select an activity that must finish before this one can start"
                : "Select an activity that depends on this one"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            {/* Activity Selection - Using hierarchical searchable picker */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Select Activity <span className="text-red-500">*</span>
              </Label>
              <RelationshipPicker
                wbsTree={wbsTree}
                activitiesByWbs={activitiesByWbs}
                selectedActivityId={newRelation.activityId}
                onSelect={(activityId) =>
                  setNewRelation({ ...newRelation, activityId })
                }
                excludeActivityId={showRelationshipsFor}
                placeholder={`Search ${addRelationType === "predecessor" ? "predecessor" : "successor"}...`}
              />
            </div>

            {/* Relationship Type */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Relationship Type</Label>
              <Select
                value={newRelation.relationshipType}
                onValueChange={(value) =>
                  setNewRelation({ ...newRelation, relationshipType: value })
                }
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FS">FS - Finish-to-Start</SelectItem>
                  <SelectItem value="SS">SS - Start-to-Start</SelectItem>
                  <SelectItem value="FF">FF - Finish-to-Finish</SelectItem>
                  <SelectItem value="SF">SF - Start-to-Finish</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400">
                {
                  {
                    FS: "Predecessor must finish before successor starts",
                    SS: "Both activities start together",
                    FF: "Both activities finish together",
                    SF: "Predecessor must start before successor finishes",
                  }[newRelation.relationshipType]
                }
              </p>
            </div>

            {/* Lag/Lead Days */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Lag/Lead (days)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={newRelation.lagDays}
                  onChange={(e) =>
                    setNewRelation({
                      ...newRelation,
                      lagDays: parseInt(e.target.value) || 0,
                    })
                  }
                  className="h-8 text-sm w-24"
                />
                <span className="text-xs text-slate-400">
                  {newRelation.lagDays > 0
                    ? `+${newRelation.lagDays} day lag`
                    : newRelation.lagDays < 0
                      ? `${newRelation.lagDays} day lead`
                      : "No lag/lead"}
                </span>
              </div>
              <p className="text-[10px] text-slate-400">
                Positive = lag (delay), Negative = lead (overlap)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowAddRelationDialog(false)}
              disabled={savingRelation}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={savingRelation || !newRelation.activityId}
              onClick={handleCreateRelationship}
            >
              {savingRelation ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Link className="mr-1.5 h-3.5 w-3.5" />
                  Add Relationship
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Resource Dialog (MODULE O-2) */}
      <Dialog
        open={showAddResourceDialog}
        onOpenChange={setShowAddResourceDialog}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-600" />
              Assign Resource
            </DialogTitle>
            <DialogDescription className="text-xs">
              Assign a resource to this activity. This is metadata only - does
              not affect schedule dates.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Resource</Label>
              <Select
                value={newResourceAssignment.resource_id}
                onValueChange={(val) =>
                  setNewResourceAssignment((prev) => ({
                    ...prev,
                    resource_id: val,
                  }))
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select a resource..." />
                </SelectTrigger>
                <SelectContent>
                  {resources.length === 0 ? (
                    <div className="text-xs text-slate-400 p-2 text-center">
                      No resources created yet.
                      <Button
                        variant="link"
                        className="p-0 h-auto text-xs text-emerald-600"
                        onClick={() => {
                          setShowAddResourceDialog(false);
                          setShowCreateResourceDialog(true);
                        }}
                      >
                        Create one →
                      </Button>
                    </div>
                  ) : (
                    resources.map((resource) => {
                      const IconComponent = getResourceTypeIcon(
                        resource.resource_type,
                      );
                      return (
                        <SelectItem key={resource.id} value={resource.id}>
                          <div className="flex items-center gap-2">
                            <IconComponent className="h-3.5 w-3.5 text-emerald-500" />
                            <span>{resource.name}</span>
                            <span className="text-slate-400">
                              ({resource.resource_type})
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Units</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0.1"
                  step="0.5"
                  value={newResourceAssignment.units}
                  onChange={(e) =>
                    setNewResourceAssignment((prev) => ({
                      ...prev,
                      units: parseFloat(e.target.value) || 1,
                    }))
                  }
                  className="h-9 w-24"
                />
                <span className="text-xs text-slate-400">units assigned</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowAddResourceDialog(false)}
              disabled={savingResource}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={savingResource || !newResourceAssignment.resource_id}
              onClick={() => handleAssignResource(showResourcesFor)}
            >
              {savingResource ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Users className="mr-1.5 h-3.5 w-3.5" />
                  Assign Resource
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Resource Dialog (MODULE O-2) */}
      <Dialog
        open={showCreateResourceDialog}
        onOpenChange={setShowCreateResourceDialog}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Plus className="h-4 w-4 text-emerald-600" />
              Create New Resource
            </DialogTitle>
            <DialogDescription className="text-xs">
              Create a resource that can be assigned to activities in this
              schedule.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Resource Name *</Label>
              <Input
                placeholder="e.g., Crew A, Excavator, Steel"
                value={newResource.name}
                onChange={(e) =>
                  setNewResource((prev) => ({ ...prev, name: e.target.value }))
                }
                className="h-9"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Resource Type</Label>
              <Select
                value={newResource.resource_type}
                onValueChange={(val) =>
                  setNewResource((prev) => ({ ...prev, resource_type: val }))
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">
                    <div className="flex items-center gap-2">
                      <Users className="h-3.5 w-3.5 text-blue-500" />
                      Person
                    </div>
                  </SelectItem>
                  <SelectItem value="equipment">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-3.5 w-3.5 text-orange-500" />
                      Equipment
                    </div>
                  </SelectItem>
                  <SelectItem value="material">
                    <div className="flex items-center gap-2">
                      <Package className="h-3.5 w-3.5 text-emerald-500" />
                      Material
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400">
                You can also type custom types directly after selection
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Unit of Measure (optional)</Label>
              <Input
                placeholder="e.g., hours, each, tons, days"
                value={newResource.unit}
                onChange={(e) =>
                  setNewResource((prev) => ({ ...prev, unit: e.target.value }))
                }
                className="h-9"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowCreateResourceDialog(false)}
              disabled={savingResource}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={savingResource || !newResource.name.trim()}
              onClick={handleCreateResource}
            >
              {savingResource ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Create Resource
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Activity Panel removed - replaced by popup dialog in SchedulingScreen */}

      {/* Bulk Edit Side Panel */}
      <BulkEditPanel
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        selectedActivities={
          // Use the post-filter activity collection so the panel sees the
          // same fields the user is looking at (current calendar_id, etc.)
          getAllActivities.filter((a) => selectedActivityIds.has(a.id))
        }
        scheduleId={activeSchedule?.id}
        projectCalendars={projectCalendars}
        onApplied={({ field, displayName, newValue, edits, scheduleId }) => {
          // Push a single composite undo entry. handleGlobalUndo in
          // SchedulingScreen detects type==='bulk' and dispatches the bulk
          // PATCH grouped by old value.
          setUndoStack((prev) => {
            const newEntry = {
              id: Date.now(),
              type: "bulk",
              field,
              displayName,
              edits, // [{activity_id, old_value}, ...]
              newValue,
              scheduleId,
              activityCount: edits.length,
              timestamp: new Date().toISOString(),
            };
            return [newEntry, ...prev].slice(0, 20);
          });
          // Clear selection and refresh data so the new values render.
          clearActivitySelection();
          onActivityChange?.();
        }}
      />

      {/* WBS Right-Click Context Menu */}
      {wbsContextMenu && (
        <div
          className="fixed z-[100] bg-white border border-slate-200 rounded-md shadow-lg py-1 min-w-[180px]"
          style={{ top: wbsContextMenu.y, left: wbsContextMenu.x }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
            onClick={() => {
              setWbsCommentsTarget(wbsContextMenu.node);
              setWbsContextMenu(null);
            }}
            data-testid="wbs-context-comments"
          >
            <MessageSquare className="h-3.5 w-3.5 text-sky-500" />
            Comments
            {commentCounts[wbsContextMenu.node.id] > 0 && (
              <span className="ml-auto text-[10px] text-slate-400 tabular-nums">
                {commentCounts[wbsContextMenu.node.id]}
              </span>
            )}
          </button>
          {(onCopyWbs || (onPasteWbs && clipboardWbs)) && (
            <div className="border-t border-slate-100 my-1" />
          )}
          {onCopyWbs && (
            <button
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
              onClick={() => {
                onCopyWbs(wbsContextMenu.node);
                setWbsContextMenu(null);
              }}
            >
              <Copy className="h-3.5 w-3.5 text-indigo-500" />
              Copy WBS
              <span className="ml-auto text-[10px] text-slate-400">Ctrl+C</span>
            </button>
          )}
          {onPasteWbs && clipboardWbs && (
            <>
              <div className="border-t border-slate-100 my-1" />
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-emerald-50"
                onClick={() => {
                  onPasteWbs(wbsContextMenu.node);
                  setWbsContextMenu(null);
                }}
              >
                <ClipboardPaste className="h-3.5 w-3.5 text-emerald-500" />
                Paste "{clipboardWbs.node.name}" here
                <span className="ml-auto text-[10px] text-slate-400">
                  Ctrl+V
                </span>
              </button>
            </>
          )}
        </div>
      )}

      {/* WBS Comments Dialog */}
      <Dialog
        open={!!wbsCommentsTarget}
        onOpenChange={(open) => !open && setWbsCommentsTarget(null)}
      >
        <DialogContent className="sm:max-w-[480px] h-[440px] flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 pt-4 pb-2 shrink-0">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <MessageSquare className="h-4 w-4 text-sky-600" />
              Comments — {wbsCommentsTarget?.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Discussion on WBS {wbsCommentsTarget?.code}. Use @ to mention an
              org member.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {wbsCommentsTarget && (
              <CommentsPanel
                entityType="wbs"
                entityId={wbsCommentsTarget.id}
                onCountChange={onCommentCountChange}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedScheduleView;
